/**
 * Module Links Registry — for QuickLinksPanel.
 *
 * Maps every L2 module (and its key L3 sub-modules) under Category Management
 * and Supplier Management to:
 *   • externals: authoritative external portals, regulators, data sources
 *   • internals: related iPCLM L2 modules that consume or produce this data
 *
 * Used by <QuickLinksPanel moduleKey="..." /> on each L2 page.
 */

export const MODULE_LINKS = {
  // ═══════════════════════ CATEGORY MANAGEMENT (Module 2.x) ═══════════════════════
  taxonomy: {
    title: 'Taxonomy & Hierarchy (2.1)',
    externals: [
      { label: 'UNSPSC Official Codeset', url: 'https://www.unspsc.org/codeset-downloads', note: 'Global procurement taxonomy' },
      { label: 'eClass Standard', url: 'https://www.eclass.eu/en/', note: 'European cross-industry product taxonomy' },
      { label: 'NIGP Commodity Codes', url: 'https://www.nigp.org/code', note: 'US public sector procurement codes' },
      { label: 'GS1 Global Product Classification', url: 'https://www.gs1.org/standards/gpc', note: 'Retail/CPG product hierarchies' },
    ],
    internals: [
      { label: 'Spend Analytics', path: '/app/scm/spend-cube', why: 'Spend cube rolls up using this taxonomy' },
      { label: 'Kraljic Matrix', path: '/app/scm/kraljic', why: 'Categories mapped to Kraljic quadrants' },
      { label: 'AVL Long-List', path: '/app/sourcing/supplier-invitation', why: 'AVL entries reference category nodes' },
      { label: 'Diversity & Inclusion', path: '/app/srm/diversity', why: 'Spend attribution by category L1/L2' },
    ],
    authoring: {
      where: 'Taxonomy & Hierarchy admin (this page)',
      path: '/app/scm/taxonomy',
      action: 'Add / edit category node',
      note: 'Category nodes (L1-L5) are authored here. Stored in `categories`. UNSPSC import jobs run from Admin → Import Master Data.',
    },
  },
  'spend-cube': {
    title: 'Spend Analytics (2.2)',
    externals: [
      { label: 'World Bank Open Data', url: 'https://data.worldbank.org', note: 'GDP, inflation, country economic indicators' },
      { label: 'FRED Economic Data', url: 'https://fred.stlouisfed.org', note: 'Commodity & wage indices' },
      { label: 'OECD Procurement Data', url: 'https://www.oecd.org/governance/public-procurement/', note: 'International procurement benchmarks' },
      { label: 'Reserve Bank of India FX Reference', url: 'https://www.rbi.org.in/Scripts/ReferenceRateArchive.aspx', note: 'Authoritative INR FX rates' },
    ],
    internals: [
      { label: 'Taxonomy & Hierarchy', path: '/app/scm/taxonomy', why: 'Spend rolls up by taxonomy nodes' },
      { label: 'Kraljic Matrix', path: '/app/scm/kraljic', why: 'Spend feeds Kraljic quadrant assignment' },
      { label: 'Diversity & Inclusion', path: '/app/srm/diversity', why: 'Diversity spend % is a slice of the spend cube' },
      { label: 'P2P Dashboard', path: '/app/p2p', why: 'PO / invoice transactions are the spend source' },
    ],
    authoring: {
      where: 'Read-only — facts computed from Invoices + POs nightly',
      path: '/app/p2p',
      action: 'Open Invoice Workbench',
      note: 'Spend facts (`spend_facts_l4`) are derived from posted invoices + POs. Authoring of source data happens in P2P. Re-cube job runs nightly via APScheduler.',
    },
  },
  kraljic: {
    title: 'Kraljic Matrix (2.3)',
    externals: [
      { label: 'Kraljic Original HBR Paper', url: 'https://hbr.org/1983/09/purchasing-must-become-supply-management', note: 'The 1983 source — Peter Kraljic' },
      { label: 'CIPS Knowledge Hub', url: 'https://www.cips.org/knowledge/', note: 'Procurement strategy guidance' },
      { label: 'McKinsey Procurement 4.0', url: 'https://www.mckinsey.com/capabilities/operations/our-insights', note: 'Strategy benchmarks' },
    ],
    internals: [
      { label: 'Spend Analytics', path: '/app/scm/spend-cube', why: 'Spend amount drives x-axis' },
      { label: 'Risk Management', path: '/app/srm/risk', why: 'Supply risk drives y-axis' },
      { label: 'Strategy Documents', path: '/app/scm/strategy-doc', why: 'Quadrant assignment drives strategy choice' },
      { label: 'Sourcing Strategy & Event Planning', path: '/app/sourcing/events', why: 'Sourcing event type derived from quadrant' },
    ],
    authoring: {
      where: 'Kraljic Matrix (this page) — Category Manager',
      path: '/app/scm/kraljic',
      action: 'Drag category onto quadrant',
      note: 'Quadrant assignments authored here. Auto-suggested by spend × supply-risk; manual override goes to `kraljic_assignments`.',
    },
  },
  'strategy-doc': {
    title: 'Category Strategy Documents (2.4)',
    externals: [
      { label: 'A.T. Kearney Procurement Maturity Model', url: 'https://www.kearney.com/operations-performance-transformation/procurement', note: 'Maturity benchmark framework' },
      { label: 'World Economic Forum Supply Chain Insights', url: 'https://www.weforum.org/topics/supply-chains/', note: 'Macro trend reporting' },
    ],
    internals: [
      { label: 'Kraljic Matrix', path: '/app/scm/kraljic', why: 'Quadrant placement feeds strategy template' },
      { label: 'Market Intelligence', path: '/app/scm/market-intel', why: 'Market scan informs strategy' },
      { label: 'Sourcing Strategy & Event Planning', path: '/app/sourcing/events', why: 'Strategy doc shapes RFx events' },
      { label: 'Demand Forecasting', path: '/app/scm/demand', why: 'Forecast volume sizes the strategy' },
    ],
    authoring: {
      where: 'Category Strategy Documents (this page)',
      path: '/app/scm/strategy-doc',
      action: 'Create / edit strategy doc',
      note: 'Strategy templates filled per category here. Stored in `category_strategies`. AI-assisted generation via Cognara.',
    },
  },
  'market-intel': {
    title: 'Market Intelligence (2.5)',
    externals: [
      { label: 'S&P Capital IQ', url: 'https://www.capitaliq.com', note: 'Public-company financials & supplier benchmarks' },
      { label: 'Crunchbase', url: 'https://www.crunchbase.com', note: 'Startup / private-company scouting' },
      { label: 'Statista Industry Reports', url: 'https://www.statista.com', note: 'Market size, growth, share' },
      { label: 'Trading Economics Commodities', url: 'https://tradingeconomics.com/commodities', note: 'Real-time commodity indices' },
      { label: 'LME Metals Pricing', url: 'https://www.lme.com', note: 'Authoritative metals reference prices' },
      { label: 'PIRA Energy / S&P Platts', url: 'https://www.spglobal.com/commodityinsights/', note: 'Energy & petrochemicals' },
    ],
    internals: [
      { label: 'Strategy Documents', path: '/app/scm/strategy-doc', why: 'Market scan feeds strategy' },
      { label: 'Demand Forecasting', path: '/app/scm/demand', why: 'Macro indicators inform forecast' },
      { label: 'Risk Management', path: '/app/srm/risk', why: 'Commodity & geopolitical risk' },
    ],
    authoring: {
      where: 'Market Intelligence (this page) + external feeds',
      path: '/app/scm/market-intel',
      action: 'Add intel note / run scan',
      note: 'Manual notes + AI market scans authored here. Stored in `market_intelligence`. External feeds (LME, S&P Platts) cached server-side.',
    },
  },
  demand: {
    title: 'Demand Forecasting (2.6)',
    externals: [
      { label: 'IBP / S&OP Frameworks (APICS)', url: 'https://www.ascm.org', note: 'Integrated business planning' },
      { label: 'Bureau of Labor Statistics — PPI', url: 'https://www.bls.gov/ppi/', note: 'US producer-price inflation' },
      { label: 'Eurostat HICP', url: 'https://ec.europa.eu/eurostat', note: 'EU price indices for forecasting' },
      { label: 'IEA World Energy Outlook', url: 'https://www.iea.org/reports/world-energy-outlook', note: 'Long-term energy demand' },
    ],
    internals: [
      { label: 'Market Intelligence', path: '/app/scm/market-intel', why: 'Macro indicators feed forecast' },
      { label: 'Strategy Documents', path: '/app/scm/strategy-doc', why: 'Forecast sizes the strategy' },
      { label: 'P2P Dashboard', path: '/app/p2p', why: 'Forecast triggers PR creation' },
    ],
    authoring: {
      where: 'Demand Forecasting (this page)',
      path: '/app/scm/demand',
      action: 'Add forecast / import history',
      note: 'Forecast models authored here. Stored in `demand_forecasts`. Historical PR + PO data is the input.',
    },
  },

  // ═══════════════════════ SUPPLIER MANAGEMENT (Module 3.x) ═══════════════════════
  registration: {
    title: 'Registration & Onboarding (3.1)',
    externals: [
      { label: 'GLEIF (Global LEI Lookup)', url: 'https://search.gleif.org', note: 'Legal Entity Identifier validation' },
      { label: 'D&B D-U-N-S Lookup', url: 'https://www.dnb.com/duns-number/lookup.html', note: 'D-U-N-S number search' },
      { label: 'OFAC SDN Sanctions Search', url: 'https://sanctionssearch.ofac.treas.gov', note: 'US Treasury sanctions screening' },
      { label: 'EU Consolidated Sanctions', url: 'https://www.sanctionsmap.eu', note: 'EU sanctions list' },
      { label: 'UK Companies House', url: 'https://find-and-update.company-information.service.gov.uk', note: 'UK company registration data' },
      { label: 'India MCA Master Data', url: 'https://www.mca.gov.in', note: 'India corporate registry' },
      { label: 'SEC EDGAR', url: 'https://www.sec.gov/edgar', note: 'US public-company filings' },
    ],
    internals: [
      { label: 'Qualification & Approval', path: '/app/srm/qualification', why: 'Registration is gate 1; qualification is gate 2' },
      { label: 'Risk Management', path: '/app/srm/risk', why: 'Initial sanctions/PEP/financial-distress screening' },
      { label: 'Data Governance', path: '/app/srm/data-governance', why: 'Onboarded supplier becomes the golden record' },
      { label: 'Diversity & Inclusion', path: '/app/srm/diversity', why: 'Diversity classifications captured at onboarding' },
      { label: 'Supplier Collaboration', path: '/app/srm/collaboration', why: 'Approved supplier gets portal access with role-based user accounts' },
    ],
    authoring: {
      where: 'Supplier Registration Wizard (intake) + Supplier Portal self-service',
      path: '/app/srm/registration',
      action: 'Invite / register supplier',
      note: 'Supplier master record authored here. Stored in `suppliers`. Portal self-registration also lands in this collection (pending approval).',
    },
  },
  qualification: {
    title: 'Qualification & Category Approval (3.2)',
    externals: [
      { label: 'ISO 9001 Certificate Validator', url: 'https://www.iso.org/certification.html', note: 'Quality cert verification' },
      { label: 'ISO 14001 / 45001 / 27001', url: 'https://www.iso.org/standards.html', note: 'Env / OH&S / InfoSec certs' },
      { label: 'IATF 16949 (Automotive)', url: 'https://www.iatfglobaloversight.org', note: 'Automotive quality cert directory' },
      { label: 'NABL Lab Accreditation (India)', url: 'https://nabl-india.org', note: 'Indian lab cert directory' },
      { label: 'IAF CertSearch', url: 'https://www.iafcertsearch.org', note: 'Global ISO cert lookup' },
    ],
    internals: [
      { label: 'Registration & Onboarding', path: '/app/srm/registration', why: 'Qualification follows registration' },
      { label: 'Risk Management', path: '/app/srm/risk', why: 'Qual checks include risk scoring' },
      { label: 'Supplier Identification & Invitation', path: '/app/sourcing/supplier-invitation', why: 'Qualified suppliers enter the AVL' },
      { label: 'Performance Management', path: '/app/srm/performance', why: 'Qualified suppliers move into perf reviews' },
      { label: 'Supplier Collaboration', path: '/app/srm/collaboration', why: 'CAR 8D/5-Why responses captured via portal' },
    ],
    authoring: {
      where: 'Qualification Workflow (this page) — Category Manager',
      path: '/app/srm/qualification',
      action: 'Run qualification on supplier',
      note: 'Qualification scores + assessment responses authored here. Stored in `qualification_records`. Portal users author certificate uploads.',
    },
  },
  performance: {
    title: 'Performance Management (3.3)',
    externals: [
      { label: 'EcoVadis Scorecards', url: 'https://ecovadis.com', note: 'CSR & sustainability rating partner' },
      { label: 'Achilles Verification', url: 'https://www.achilles.com', note: 'Supplier pre-qualification / performance data' },
      { label: 'NQC SupplierAssurance', url: 'https://www.nqc.com', note: 'Automotive supplier performance database' },
      { label: 'OEM Scorecards (Toyota TS, GM SoR)', url: 'https://www.bsiamerica.com/en-us/Industries-and-sectors/Automotive/', note: 'OEM-specific scorecard methods' },
    ],
    internals: [
      { label: 'Qualification', path: '/app/srm/qualification', why: 'Perf reviews triggered by qualified status' },
      { label: 'Relationship Management', path: '/app/srm/relationship', why: 'Perf score feeds SRM tier' },
      { label: 'Risk Management', path: '/app/srm/risk', why: 'Repeated underperformance becomes a risk signal' },
      { label: 'Supplier Identification & Invitation', path: '/app/sourcing/supplier-invitation', why: 'Poor performers may be suspended' },
      { label: 'Supplier Collaboration', path: '/app/srm/collaboration', why: '6-dimension radar chart visible to supplier on portal' },
    ],
    authoring: {
      where: 'Performance scorecards (this page) + auto-feeds from OTD/quality',
      path: '/app/srm/performance',
      action: 'Run scorecard cycle',
      note: 'Performance scores authored here by Category Manager + auto-computed from GRN/Invoice/Claim signals. Stored in `supplier_performance`.',
    },
  },
  relationship: {
    title: 'Relationship Management (3.4) — incl. Health Map',
    externals: [
      { label: 'CIPS Strategic Supplier Mgmt', url: 'https://www.cips.org/knowledge/procurement-topics-and-skills/strategy-and-policy/strategic-supplier-management/', note: 'SRM framework reference' },
      { label: 'Gartner Magic Quadrant for Procurement', url: 'https://www.gartner.com', note: 'Industry SRM platform benchmark' },
      { label: 'NPS / CES Benchmarking', url: 'https://www.netpromoter.com', note: 'NPS methodology' },
    ],
    internals: [
      { label: 'Performance Management', path: '/app/srm/performance', why: 'Perf feeds health score' },
      { label: 'Risk Management', path: '/app/srm/risk', why: 'Risk feeds health score' },
      { label: 'Data Governance', path: '/app/srm/data-governance', why: 'DQ band feeds health score' },
      { label: 'Diversity & Inclusion', path: '/app/srm/diversity', why: 'Diversity classification is part of relationship profile' },
      { label: 'Supplier Collaboration', path: '/app/srm/collaboration', why: 'Messages, JBPs, broadcasts and surveys served through portal' },
    ],
    authoring: {
      where: 'Relationship Hub (this page) + Performance + Risk + DQ feeds',
      path: '/app/srm/relationship',
      action: 'Open supplier 360',
      note: 'Relationship tier + health authored as a composite. Manual tier override in this page; composite computed nightly from Performance + Risk + DQ + Diversity signals.',
    },
  },
  risk: {
    title: 'Risk Management (3.5)',
    externals: [
      { label: 'OFAC SDN', url: 'https://sanctionssearch.ofac.treas.gov', note: 'US sanctions' },
      { label: 'EU Consolidated Sanctions', url: 'https://www.sanctionsmap.eu', note: 'EU sanctions' },
      { label: 'UN Consolidated List', url: 'https://www.un.org/securitycouncil/content/un-sc-consolidated-list', note: 'UN Security Council sanctions' },
      { label: 'Creditsafe', url: 'https://www.creditsafe.com', note: 'Financial-distress signals' },
      { label: 'D&B Risk Analytics', url: 'https://www.dnb.com/products/finance-credit-risk/dnb-risk-analytics-supplier.html', note: 'Supplier-risk data' },
      { label: 'BitSight Cyber Ratings', url: 'https://www.bitsight.com', note: 'Cybersecurity supplier ratings' },
      { label: 'SecurityScorecard', url: 'https://securityscorecard.com', note: 'Cyber-risk ratings' },
      { label: 'EcoVadis ESG Ratings', url: 'https://ecovadis.com', note: 'ESG / sustainability' },
      { label: 'Sedex SMETA', url: 'https://www.sedex.com', note: 'Ethical-trade audit data' },
      { label: 'Sustainalytics ESG', url: 'https://www.sustainalytics.com', note: 'ESG risk research' },
      { label: 'USGS Earthquake Feed', url: 'https://earthquake.usgs.gov/earthquakes/feed/', note: 'Real-time seismic events' },
      { label: 'World Bank WGI', url: 'https://info.worldbank.org/governance/wgi/', note: 'Governance indicators' },
      { label: 'LexisNexis Risk Solutions', url: 'https://risk.lexisnexis.com', note: 'Adverse media + PEP' },
      { label: 'Have I Been Pwned API', url: 'https://haveibeenpwned.com/API/v3', note: 'Breach exposure check' },
    ],
    internals: [
      { label: 'Registration & Onboarding', path: '/app/srm/registration', why: 'Initial screening at onboarding' },
      { label: 'Performance Management', path: '/app/srm/performance', why: 'Underperformance feeds risk score' },
      { label: 'Relationship Management', path: '/app/srm/relationship', why: 'Risk score = 25% of health score' },
      { label: 'Data Governance', path: '/app/srm/data-governance', why: 'Risk events stored as supplier evidence' },
      { label: 'Supplier Collaboration', path: '/app/srm/collaboration', why: 'Portal MFA + lockout failure events surface as access-risk signals' },
    ],
    authoring: {
      where: 'Risk Management (this page) — Risk Officer',
      path: '/app/srm/risk',
      action: 'Run risk scan / add finding',
      note: 'Risk findings + scores authored here. Sources: sanctions screen, financial distress, geopolitical, ESG, cyber. Stored in `risk_findings` + `risk_scores`.',
    },
  },
  'data-governance': {
    title: 'Information & Data Management (3.6)',
    externals: [
      { label: 'GLEIF (Legal Entity Identifier)', url: 'https://search.gleif.org', note: 'LEI lookup for golden record' },
      { label: 'BvD Orbis', url: 'https://www.bvdinfo.com/en-gb/our-products/data/international/orbis', note: 'Corporate hierarchy enrichment' },
      { label: 'D&B D-U-N-S Lookup', url: 'https://www.dnb.com/duns-number/lookup.html', note: 'D-U-N-S validation' },
      { label: 'SAP S/4HANA Supplier Master', url: 'https://help.sap.com/docs/SAP_S4HANA_CLOUD', note: 'ERP integration target' },
      { label: 'Oracle ERP Cloud — Suppliers', url: 'https://docs.oracle.com/en/cloud/saas/procurement/', note: 'ERP integration target' },
      { label: 'Microsoft Dynamics 365 Vendors', url: 'https://learn.microsoft.com/en-us/dynamics365/finance/accounts-payable/', note: 'ERP integration target' },
      { label: 'GDPR / DPIA Toolkit (ICO UK)', url: 'https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/', note: 'Banking data privacy controls' },
      { label: 'ISO 8000 Data Quality', url: 'https://www.iso.org/standard/50798.html', note: 'Master-data quality standard' },
    ],
    internals: [
      { label: 'Registration & Onboarding', path: '/app/srm/registration', why: 'Onboarded supplier becomes the golden record' },
      { label: 'Diversity & Inclusion', path: '/app/srm/diversity', why: 'Reuses document vault + expiry tracker' },
      { label: 'Risk Management', path: '/app/srm/risk', why: 'Risk events are MDG attestations' },
      { label: 'P2P Dashboard', path: '/app/p2p', why: 'Banking changes block PO release during cooling period' },
      { label: 'Performance Management', path: '/app/srm/performance', why: 'DQ score is a perf dimension' },
      { label: 'Supplier Collaboration', path: '/app/srm/collaboration', why: 'Portal change-request flow updates MDG golden record' },
    ],
    authoring: {
      where: 'Data Governance (this page) — Data Steward',
      path: '/app/srm/data-governance',
      action: 'Resolve duplicate / approve change',
      note: 'Master data steward decisions authored here. Stored in `mdm_decisions`. Golden-record changes are audit-logged.',
    },
  },
  diversity: {
    title: 'Diversity & Inclusion (3.7)',
    externals: [
      { label: 'SAM.gov (US Federal)', url: 'https://sam.gov', note: 'US federal contractor / cert / ISR submission' },
      { label: 'NMSDC (US MBE)', url: 'https://nmsdc.org', note: 'Minority Business Enterprise certifications' },
      { label: 'WBENC (US WBE)', url: 'https://www.wbenc.org', note: 'Women-owned business cert' },
      { label: 'NGLCC (US LGBTBE)', url: 'https://www.nglcc.org', note: 'LGBT business enterprise cert' },
      { label: 'SBA Small Business Search', url: 'https://dsbs.sba.gov', note: 'SBA / 8(a) / HUBZone / SDVOSB' },
      { label: 'India Udyam (MSME)', url: 'https://udyamregistration.gov.in', note: 'India MSME registration' },
      { label: 'GeM (Government e-Marketplace, India)', url: 'https://gem.gov.in', note: 'India public procurement portal' },
      { label: 'Supply Nation (Australia)', url: 'https://supplynation.org.au', note: 'Indigenous business cert (AU)' },
      { label: 'CCAB (Canada)', url: 'https://www.ccab.com', note: 'Canadian indigenous business cert' },
      { label: 'EU Social Enterprises', url: 'https://single-market-economy.ec.europa.eu/sectors/proximity-and-social-economy_en', note: 'EU social-economy enterprises' },
      { label: 'UK PPN 06/20 Social Value', url: 'https://www.gov.uk/government/publications/procurement-policy-note-0620-taking-account-of-social-value-in-the-award-of-central-government-contracts', note: 'UK Social Value Model' },
      { label: 'FAR 52.219-9 (US ISR/SSR)', url: 'https://www.acquisition.gov/far/52.219-9', note: 'US small-business subcontracting plan reporting' },
    ],
    internals: [
      { label: 'Registration & Onboarding', path: '/app/srm/registration', why: 'Diversity classifications captured at onboarding' },
      { label: 'Data Governance', path: '/app/srm/data-governance', why: 'Cert files in shared document vault; expiry tracker' },
      { label: 'Risk Management', path: '/app/srm/risk', why: 'Cert misrepresentation is a fraud-risk signal' },
      { label: 'Relationship Management', path: '/app/srm/relationship', why: 'Diversity classification visible in supplier profile' },
      { label: 'Spend Analytics', path: '/app/scm/spend-cube', why: 'Diversity spend % is a spend-cube slice' },
      { label: 'Supplier Identification & Invitation', path: '/app/sourcing/supplier-invitation', why: 'Diverse-first tie-breaker in guided buying' },
      { label: 'Supplier Collaboration', path: '/app/srm/collaboration', why: 'Diversity badges visible on directory profile' },
    ],
    authoring: {
      where: 'Diversity & Inclusion (this page) — Diversity Officer',
      path: '/app/srm/diversity',
      action: 'Verify cert / add diversity tag',
      note: 'Diversity classifications authored here. Cert validation via NMSDC / WBENC / GLEIF. Stored in `diversity_certifications`.',
    },
  },
  'portal-collab': {
    title: 'Supplier Collaboration & Portal (3.8)',
    externals: [
      { label: 'DHL Tracking API', url: 'https://developer.dhl.com/api-reference/shipment-tracking', note: 'Live shipment tracking integration' },
      { label: 'FedEx Track API', url: 'https://developer.fedex.com/api/en-us/catalog/track.html', note: 'Live FedEx shipment tracking' },
      { label: 'UPS Track API', url: 'https://developer.ups.com/api/reference?loc=en_US#tag/Tracking', note: 'Live UPS tracking integration' },
      { label: 'Maersk Track & Trace', url: 'https://api.maersk.com/track-and-trace-private/', note: 'Ocean freight tracking' },
      { label: 'Resend Email API', url: 'https://resend.com/docs', note: 'Transactional emails to suppliers (unread msg, SLA reminders)' },
      { label: 'GSTN India (gst.gov.in)', url: 'https://www.gst.gov.in', note: 'India GSTIN registry, HSN/SAC official codes' },
      { label: 'GeM (Government e-Marketplace, India)', url: 'https://gem.gov.in', note: 'India public-sector e-procurement portal' },
      { label: 'Peppol BIS Billing 3.0', url: 'https://docs.peppol.eu/poacc/billing/3.0/', note: 'EU e-invoice exchange standard' },
      { label: 'PoTOTP (RFC 6238)', url: 'https://datatracker.ietf.org/doc/html/rfc6238', note: 'Time-based one-time password — MFA standard' },
      { label: 'WCAG 2.1 AA', url: 'https://www.w3.org/WAI/WCAG21/quickref/?currentsidebar=%23col_overview&levels=aaa', note: 'Accessibility standard for portal UI' },
      { label: 'CIPS Supplier Collaboration', url: 'https://www.cips.org/knowledge/procurement-topics-and-skills/supplier-management/', note: 'Industry best-practice framework' },
    ],
    internals: [
      { label: 'Registration & Onboarding', path: '/app/srm/registration', why: 'Portal user accounts created from registration outcome' },
      { label: 'Qualification & Approval', path: '/app/srm/qualification', why: 'CAR responses (8D/5-Why) submitted via portal' },
      { label: 'Performance Management', path: '/app/srm/performance', why: '6-dim scorecards rendered on portal radar chart' },
      { label: 'Relationship Management', path: '/app/srm/relationship', why: 'Messages, JBPs and surveys flow through portal' },
      { label: 'Data Governance', path: '/app/srm/data-governance', why: 'Portal self-service drives golden-record change requests' },
      { label: 'Diversity & Inclusion', path: '/app/srm/diversity', why: 'Diversity certs uploaded + Tier-2 reports via portal' },
      { label: 'Risk Management', path: '/app/srm/risk', why: 'Portal MFA + lockout drives access security signals' },
      { label: 'Sourcing Strategy & Event Planning', path: '/app/sourcing/events', why: 'RFx invitations, bidding and auctions served via portal' },
      { label: 'P2P Dashboard', path: '/app/p2p', why: 'PO acks, ASNs and invoices originate / land via portal' },
    ],
    authoring: {
      where: 'Supplier Collaboration Hub (this page) + Supplier Portal',
      path: '/app/srm/collaboration',
      action: 'Send broadcast / start JBP',
      note: 'JBPs, messages, broadcasts, surveys authored here by buyer; replies authored by supplier on Portal. Stored in `collaboration_threads`.',
    },
  },

  'bid-submission': {
    title: 'Bid / Proposal Submission (4.4)',
    externals: [
      { label: 'EU Procurement Directive 2014/24/EU — Art. 22, 56 (e-submission)', url: 'https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:32014L0024', note: 'EU rules on electronic submission & sealed bids' },
      { label: 'UNCITRAL Model Law — Art. 30-33 (Submission)', url: 'https://uncitral.un.org/en/texts/procurement/modellaw/public_procurement', note: 'Submission & opening procedures' },
      { label: 'WTO GPA Article XV (Tendering)', url: 'https://www.wto.org/english/tratop_e/gproc_e/gp_gpa_e.htm', note: 'Sealed-bid integrity standards' },
      { label: 'FAR Part 14 (Sealed Bidding)', url: 'https://www.acquisition.gov/far/part-14', note: 'US sealed-bid procedures' },
      { label: 'FAR Part 15 (Two-step / RFP)', url: 'https://www.acquisition.gov/far/part-15', note: 'US RFP two-envelope rules' },
      { label: 'World Bank Procurement Regulations §VI', url: 'https://thedocs.worldbank.org/en/doc/178331533065871195-0290022018/original/ProcurementRegulations.pdf', note: 'Bid receipt & opening' },
      { label: 'NIST FIPS 140-3 (Cryptographic Module Validation)', url: 'https://csrc.nist.gov/Projects/cryptographic-module-validation-program', note: 'AES-256 / Fernet compliance baseline' },
      { label: 'NIST SP 800-57 (Key Management)', url: 'https://csrc.nist.gov/publications/detail/sp/800-57-part-1/rev-5/final', note: 'Time-locked key management baseline' },
      { label: 'Cognara AI', url: 'https://www.anthropic.com', note: 'AI contamination scan' },
      { label: 'Resend (Bid receipt delivery)', url: 'https://resend.com', note: 'Receipt email dispatch' },
    ],
    internals: [
      { label: 'Sourcing Strategy & Event Planning', path: '/app/sourcing/events', why: 'Bid format template + lot structure drive the bid form' },
      { label: 'Supplier Identification & Invitation', path: '/app/sourcing/supplier-invitation', why: 'Only ACCEPTED invitees can submit bids' },
      { label: 'Clarification & Pre-Bid Management', path: '/app/sourcing/clarifications', why: 'Direct predecessor — addenda must be acknowledged' },
      { label: 'Supplier Portal — Bid Submission', path: '/app/portal/bid-submission', why: 'Supplier-facing bid entry workspace' },
      { label: 'ITT Regulatory Compliance', path: '/app/sourcing/lifecycle', why: 'Witness signatures + ms-precision deadline enforcement' },
      { label: 'Approval Inbox', path: '/app/sourcing/approval-inbox', why: 'Witness invitations + deadline-extension approvals' },
      { label: 'Notification Engine', path: '/app/sourcing/notifications', why: 'Bid receipt emails & late-attempt alerts' },
    ],
    authoring: {
      where: 'Supplier Portal (sealed bid upload) + Sourcing Monitor (admin view)',
      path: '/app/sourcing/bid-submission',
      action: 'Open Bid Monitor',
      note: 'Bids authored by suppliers on Portal (`/portal/bid-submission/{eventId}`). Sealed envelopes (TECH/COMM) stored in `bids` with SHA-256 hash.',
    },
  },

  'supplier-invitation': {
    title: 'Supplier Identification & Invitation (4.2)',
    externals: [
      { label: 'EU TED (Tenders Electronic Daily)', url: 'https://ted.europa.eu', note: 'EU contract notice publication' },
      { label: 'UK Find a Tender', url: 'https://www.find-tender.service.gov.uk', note: 'UK ITT public notice register' },
      { label: 'India CPPP', url: 'https://eprocure.gov.in', note: 'India central tender notice portal' },
      { label: 'SAM.gov (US)', url: 'https://sam.gov', note: 'US federal contract opportunities' },
      { label: 'World Bank STEP', url: 'https://step.worldbank.org', note: 'World Bank procurement notices' },
      { label: 'OFAC SDN List', url: 'https://sanctionssearch.ofac.treas.gov', note: 'US Treasury sanctions screening' },
      { label: 'UN Consolidated Sanctions', url: 'https://main.un.org/securitycouncil/en/content/un-sc-consolidated-list', note: 'UN consolidated sanctions list' },
      { label: 'D&B / Dun & Bradstreet', url: 'https://www.dnb.com', note: 'Business credit, ownership, financial health' },
      { label: 'EU eCERTIS', url: 'https://ec.europa.eu/tools/ecertis/', note: 'EU evidence/certificate equivalence' },
      { label: 'NDA Lifecycle Guidance (CIPS)', url: 'https://www.cips.org/knowledge/', note: 'NDA / confidentiality best practice' },
    ],
    internals: [
      { label: 'AVL / Qualification', path: '/app/srm/qualification', why: 'Only qualified suppliers can join the long-list' },
      { label: 'Performance Management', path: '/app/srm/performance', why: 'Performance score gates invitation eligibility' },
      { label: 'Risk Management', path: '/app/srm/risk', why: 'Risk status gates eligibility for invitation' },
      { label: 'Diversity & Inclusion', path: '/app/srm/diversity', why: 'Diverse-first nudges & set-asides applied here' },
      { label: 'Sourcing Strategy & Event Planning', path: '/app/sourcing/events', why: 'Event scope/category drives long-list' },
      { label: 'Clarification & Pre-Bid Management', path: '/app/sourcing/clarifications', why: 'Invited suppliers continue into Q&A workflow' },
      { label: 'Supplier Collaboration / Portal', path: '/app/srm/collaboration', why: 'Invitations dispatched via portal' },
    ],
    authoring: {
      where: 'Supplier Invitation (this page) — Sourcing Manager',
      path: '/app/sourcing/supplier-invitation',
      action: 'Build long-list + invite',
      note: 'Long-list authored here from AVL + qualification status. Invitations dispatched via Resend; stored in `sourcing_invitations`.',
    },
  },
  clarifications: {
    title: 'Clarification & Pre-Bid Management (4.3)',
    externals: [
      { label: 'EU Procurement Directive 2014/24/EU — Art. 53 (Clarifications)', url: 'https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:32014L0024', note: 'EU rules on equal-treatment in Q&A' },
      { label: 'UNCITRAL Model Law Art. 15-17 (Clarifications & Modifications)', url: 'https://uncitral.un.org/en/texts/procurement/modellaw/public_procurement', note: 'Anonymisation & equal-distribution standard' },
      { label: 'WTO GPA Article XV (Tendering)', url: 'https://www.wto.org/english/tratop_e/gproc_e/gp_gpa_e.htm', note: 'WTO Government Procurement Agreement' },
      { label: 'FAR Subpart 15.201 (Pre-solicitation Exchanges)', url: 'https://www.acquisition.gov/far/subpart-15.2', note: 'US FAR pre-bid communication rules' },
      { label: 'World Bank Procurement Regs §5.30-5.33 (Clarification)', url: 'https://thedocs.worldbank.org/en/doc/178331533065871195-0290022018/original/ProcurementRegulations.pdf', note: 'WB rules for addenda & deadline extensions' },
      { label: 'CIPS Knowledge — Pre-Bid Conferences', url: 'https://www.cips.org/knowledge/procurement-topics-and-skills/sourcing/', note: 'Best-practice pre-bid conduct' },
      { label: 'GeM Bid Q&A Module', url: 'https://gem.gov.in', note: 'India GeM clarification reference' },
      { label: 'ISO 44001 — Collaborative Business Relationships', url: 'https://www.iso.org/standard/72798.html', note: 'Structured supplier engagement' },
      { label: 'Resend (Notification Delivery)', url: 'https://resend.com', note: 'Outbound email dispatch provider' },
      { label: 'Cognara API', url: 'https://www.anthropic.com', note: 'AI categorisation / response drafting' },
    ],
    internals: [
      { label: 'Supplier Identification & Invitation', path: '/app/sourcing/supplier-invitation', why: 'Accepted invitees are the eligible Q&A audience' },
      { label: 'Sourcing Strategy & Event Planning', path: '/app/sourcing/events', why: 'Source event configuration & ITT compliance flag' },
      { label: 'Bid / Proposal Submission', path: '/app/sourcing/bid-submission', why: 'Clarifications close before bid window opens' },
      { label: 'Supplier Portal — Sourcing', path: '/app/sourcing/portal-interface', why: 'Suppliers submit Q&A from portal' },
      { label: 'ITT Regulatory Compliance', path: '/app/sourcing/lifecycle', why: 'Adversarial anonymisation enforced for ITT' },
      { label: 'Approval Inbox', path: '/app/sourcing/approval-inbox', why: 'Addendum & extension approvals routed here' },
      { label: 'Sourcing Calendar', path: '/app/sourcing/calendar', why: 'Pre-bid conferences appear on calendar' },
      { label: 'Notification Engine', path: '/app/sourcing/notifications', why: 'Equal-distribution email/portal dispatch' },
    ],
    authoring: {
      where: 'Clarification Hub (this page) — both buyer + supplier',
      path: '/app/sourcing/clarifications',
      action: 'Ask / answer clarification',
      note: 'Questions authored by suppliers on Portal; answers authored by buyer here. Stored in `clarifications`. Forum/private mode supported.',
    },
  },

  'sourcing-strategic': {
    title: 'Strategic Sourcing (4.x) — 16 L2 sub-modules',
    externals: [
      { label: 'CIPS Sourcing Knowledge', url: 'https://www.cips.org/knowledge/procurement-topics-and-skills/sourcing/', note: 'Sourcing methodology framework' },
      { label: 'OECD MAPS (Public Procurement)', url: 'https://www.mapsinitiative.org/', note: 'Public-sector procurement maturity assessment' },
      { label: 'UNCITRAL Model Law on Public Procurement', url: 'https://uncitral.un.org/en/texts/procurement/modellaw/public_procurement', note: 'International tendering legal framework' },
      { label: 'EU Public Procurement Directives (2014/24/EU)', url: 'https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:32014L0024', note: 'EU public sector ITT regulation' },
      { label: 'India GeM (Public Procurement)', url: 'https://gem.gov.in', note: 'India Government e-Marketplace' },
      { label: 'India CPPP (Central Public Procurement Portal)', url: 'https://eprocure.gov.in', note: 'India central tender portal' },
      { label: 'UK Find a Tender', url: 'https://www.find-tender.service.gov.uk', note: 'UK public procurement notices' },
      { label: 'TED — Tenders Electronic Daily (EU)', url: 'https://ted.europa.eu', note: 'EU tender notices portal' },
      { label: 'SAM.gov (US Federal)', url: 'https://sam.gov', note: 'US federal contract opportunities' },
      { label: 'FAR — Federal Acquisition Regulation', url: 'https://www.acquisition.gov/far/', note: 'US federal procurement regulations' },
      { label: 'World Bank Procurement Standards', url: 'https://www.worldbank.org/en/projects-operations/products-and-services/brief/procurement-new-framework', note: 'Multilateral lending procurement standards' },
      { label: 'ISO 20400 — Sustainable Procurement', url: 'https://www.iso.org/standard/63026.html', note: 'Sustainable sourcing standard' },
    ],
    internals: [
      { label: 'Category Strategy Doc', path: '/app/scm/strategy-doc', why: 'Strategy drives event-type and weighting' },
      { label: 'Kraljic Matrix', path: '/app/scm/kraljic', why: 'Quadrant drives sourcing approach (auction vs. partner)' },
      { label: 'AVL / Qualification', path: '/app/srm/qualification', why: 'Only qualified suppliers can be invited' },
      { label: 'Performance Management', path: '/app/srm/performance', why: 'Performance score gates invitation eligibility' },
      { label: 'Risk Management', path: '/app/srm/risk', why: 'Risk status gates eligibility for award' },
      { label: 'Diversity & Inclusion', path: '/app/srm/diversity', why: 'Diversity tie-breakers in award scoring' },
      { label: 'Supplier Collaboration', path: '/app/srm/collaboration', why: 'Bids, clarifications and Q&A flow through portal' },
      { label: 'Contracts Repository', path: '/app/cm/repository', why: 'Award initiates contract draft' },
      { label: 'P2P Dashboard', path: '/app/p2p', why: 'Awarded events become PRs/POs' },
    ],
    authoring: {
      where: 'Sourcing Strategy Lifecycle (this page) — Sourcing Manager',
      path: '/app/sourcing/lifecycle',
      action: 'Define strategy / draft RFx',
      note: 'Strategy artifacts authored here. Stored in `sourcing_strategies`. Cascades into RFx event creation.',
    },
  },

  'communication-engine': {
    title: 'Communication Engine (SS-4.13)',
    externals: [
      { label: 'Resend Email API Docs', url: 'https://resend.com/docs', note: 'Transactional email provider — wire RESEND_API_KEY' },
      { label: 'Twilio SMS Programmable Messaging', url: 'https://www.twilio.com/docs/messaging', note: 'SMS dispatch provider' },
      { label: 'Jinja2 Template Reference', url: 'https://jinja.palletsprojects.com/en/stable/templates/', note: 'Template syntax used in subject/body fields' },
      { label: 'SendGrid Event Webhook Schema', url: 'https://www.twilio.com/docs/sendgrid/for-developers/tracking-events/event', note: 'Bounce/open webhook contract' },
    ],
    internals: [
      { label: 'Sourcing Events', path: '/app/sourcing/events', why: 'Events publish triggers (invitation, award, addendum) to the engine' },
      { label: 'ITT Compliance', path: '/app/sourcing/itt-compliance', why: 'Standstill notices fan out via this engine' },
      { label: 'Supplier Portal Admin', path: '/app/sourcing/portal-admin', why: 'Portal notifications inbox reads from communicationLog' },
      { label: 'Sourcing Analytics', path: '/app/sourcing/analytics', why: 'Delivery + open rates feed reporting' },
    ],
    authoring: {
      where: 'Communication Engine (this page) — auto-sent + manual',
      path: '/app/sourcing/communication-engine',
      action: 'Compose / preview communication',
      note: 'Templates authored in Admin; outbound communications authored here (manual) or auto-triggered by event state changes. Stored in `comm_outbox`. Resend dispatches email.',
    },
  },
  'sourcing-analytics': {
    title: 'Sourcing Analytics & Reporting (SS-4.14)',
    externals: [
      { label: 'Hackett Group Procurement KPIs', url: 'https://www.thehackettgroup.com/insights-procurement/', note: 'Benchmarks for cycle time & savings %' },
      { label: 'World CC Contract & Sourcing Index', url: 'https://www.worldcc.com/Resources/Content-Hub', note: 'Benchmark studies' },
      { label: 'CIPS Procurement KPIs', url: 'https://www.cips.org/intelligence-hub/sustainable-and-ethical-procurement/measuring-and-reporting/key-performance-indicators-kpis', note: 'KPI taxonomy' },
    ],
    internals: [
      { label: 'Sourcing Cockpit', path: '/app/sourcing/cockpit', why: 'Cockpit funnel uses analytics aggregates' },
      { label: 'Award Recommendation', path: '/app/sourcing/award-recommendation', why: 'Savings register populated from approved awards' },
      { label: 'Framework Agreements', path: '/app/sourcing/framework-agreements', why: 'Framework utilisation feeds the utilisation widget' },
      { label: 'ITT Compliance', path: '/app/sourcing/itt-compliance', why: 'Compliance KPIs aggregated here' },
    ],
    authoring: {
      where: 'Read-only — aggregated from Sourcing Events + Bids + Awards',
      path: '/app/sourcing/events',
      action: 'Open events list',
      note: 'Sourcing analytics page is a roll-up. Underlying data authored across Sourcing Events, Bid Submission, Evaluation, and Award.',
    },
  },
  'supplier-portal-admin': {
    title: 'Supplier Portal — SS-4.15',
    externals: [
      { label: 'OWASP API Security Top 10', url: 'https://owasp.org/API-Security/editions/2023/en/0x11-t10/', note: 'Reference for portal isolation hardening' },
      { label: 'NIST SP 800-63B (Authenticator Assurance)', url: 'https://pages.nist.gov/800-63-3/sp800-63b.html', note: 'Auth strength guidance for supplier accounts' },
      { label: 'WCAG 2.2 Accessibility', url: 'https://www.w3.org/TR/WCAG22/', note: 'Public-facing portal accessibility baseline' },
    ],
    internals: [
      { label: 'Supplier Portal v2 (live)', path: '/portal-v2', why: 'The actual supplier-facing isolated workspace' },
      { label: 'Supplier Invitation', path: '/app/sourcing/supplier-invitation', why: 'Invitations populate supplier-side events' },
      { label: 'Communication Engine', path: '/app/sourcing/communication-engine', why: 'Portal inbox sourced from communicationLog' },
      { label: 'Award Recommendation', path: '/app/sourcing/award-recommendation', why: 'Award outcomes + debriefs surfaced in supplier portal' },
    ],
    authoring: {
      where: 'Supplier Portal Admin (this page) — Admin only',
      path: '/app/sourcing/portal-admin',
      action: 'Configure portal settings',
      note: 'Portal config (branding, allowed file types, idle timeout) authored here. Portal users authored under Supplier Registration.',
    },
  },
  'itt-compliance': {
    title: 'ITT Regulatory Compliance (SS-4.16)',
    externals: [
      { label: 'EU 2014/24/EU (Public Sector ITT)', url: 'https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:32014L0024', note: 'EU thresholds + procedures' },
      { label: 'TED eForms', url: 'https://op.europa.eu/en/web/eforms', note: 'Standard XML for EU notice publication' },
      { label: 'UK PCR 2015 (Public Contracts Regulations)', url: 'https://www.legislation.gov.uk/uksi/2015/102/contents/made', note: 'UK procurement law' },
      { label: 'US FAR Part 5 (Publicizing Contract Actions)', url: 'https://www.acquisition.gov/far/part-5', note: 'US federal notice rules' },
      { label: 'India GFR + GeM Rules', url: 'https://doe.gov.in/order-circular/general-financial-rules-2017', note: 'India procurement rules' },
      { label: 'SAM.gov FedConnect', url: 'https://sam.gov', note: 'US federal contract opportunities portal' },
      { label: 'UK Find a Tender Service', url: 'https://www.find-tender.service.gov.uk', note: 'UK FTS publication' },
    ],
    internals: [
      { label: 'Strategy & Planning', path: '/app/sourcing/lifecycle', why: 'Compliance record bootstrapped on strategy approval' },
      { label: 'Sourcing Events', path: '/app/sourcing/events', why: 'Threshold checks gate event publication' },
      { label: 'Award Recommendation', path: '/app/sourcing/award-recommendation', why: 'Standstill clock starts on award approval' },
      { label: 'Communication Engine', path: '/app/sourcing/communication-engine', why: 'Public notices + standstill emails fan out here' },
      { label: 'Sourcing Analytics', path: '/app/sourcing/analytics', why: 'Compliance KPIs visualised in reporting hub' },
    ],
    authoring: {
      where: 'ITT Compliance (this page) — Compliance Officer',
      path: '/app/sourcing/itt-compliance',
      action: 'Author ITT compliance template',
      note: 'Compliance templates authored here per event type. Standstill + audit-trail rules configured here. Stored in `itt_compliance_templates`.',
    },
  },

  // ═══════════════════════ REQUISITIONS, PO, P2P (Module 5–6) ═══════════════════════
  requisitions: {
    title: 'Requisitions (RQ-5)',
    externals: [
      { label: 'India GST HSN Search', url: 'https://services.gst.gov.in/services/searchhsnsac', note: 'Authoritative HSN/SAC codes for line items' },
      { label: 'World Bank Country Risk', url: 'https://www.worldbank.org/en/publication/global-economic-prospects', note: 'Country/political-risk inputs for AVL scoring' },
      { label: 'OFAC SDN List', url: 'https://sanctionssearch.ofac.treas.gov/', note: 'Sanctions screening for supplier picks' },
      { label: 'India CIN / MCA21 (MCA)', url: 'https://www.mca.gov.in/mcafoportal/viewCompanyMasterData.do', note: 'Supplier legal/registration data' },
    ],
    internals: [
      { label: 'Purchase Orders', path: '/app/po', why: 'Approved PRs convert to POs (1:1 / N:1 / 1:N)' },
      { label: 'Spend Analytics', path: '/app/scm/spend-cube', why: 'PR/PO transactions feed the spend cube' },
      { label: 'Sourcing Events', path: '/app/sourcing/events', why: 'High-value PRs trigger competitive sourcing' },
      { label: 'Supplier Identification', path: '/app/sourcing/supplier-invitation', why: 'AVL drives PR supplier recommendations' },
      { label: 'Contracts', path: '/app/contracts', why: 'Framework PRs call-off against active contracts' },
    ],
    authoring: {
      where: 'Requisitions Workbench (this page) or NLP / Catalogue intake',
      path: '/app/p2p',
      action: 'Create new PR',
      note: 'PRs originate here (manual, NLP intake, or catalogue-driven). Stored in `requisitions`. AI enrichment runs on save.',
    },
  },
  po: {
    title: 'Purchase Orders (PO-6)',
    externals: [
      { label: 'India GST E-Invoice Portal', url: 'https://einvoice1.gst.gov.in', note: 'IRN generation for B2B invoices' },
      { label: 'GLEIF LEI Lookup', url: 'https://www.gleif.org/en/lei-data/global-lei-index', note: 'Legal Entity Identifier verification' },
      { label: 'India e-Way Bill Portal', url: 'https://ewaybillgst.gov.in', note: 'Goods movement under PO' },
      { label: 'INCOTERMS 2020 (ICC)', url: 'https://iccwbo.org/business-solutions/incoterms-rules/incoterms-2020/', note: 'Authoritative INCOTERMS reference' },
    ],
    internals: [
      { label: 'Requisitions', path: '/app/requisitions', why: 'Source of PR-to-PO conversion' },
      { label: 'Goods Receipt', path: '/app/grn', why: 'GRN closes the 3-way match loop' },
      { label: 'Invoices & Payment', path: '/app/ap', why: 'Invoice matched against PO + GRN' },
      { label: 'Frameworks', path: '/app/sourcing/framework-agreements', why: 'Framework call-off POs draw on agreement caps' },
      { label: 'Contracts', path: '/app/contracts', why: 'PO change-orders cascade to contract amendments' },
    ],
    authoring: {
      where: 'PO Workbench (this page) — auto-created from approved PR or Framework call-off',
      path: '/app/po',
      action: 'Convert PR → PO',
      note: 'POs are authored automatically from approved PRs (1:1 / N:1 / 1:N). Manual PO creation also supported. Stored in `purchase_orders`.',
    },
  },
  'purchase-orders': null,  // alias resolved below

  // ═══════════════════════ CONTRACTS & CLAIMS ═══════════════════════
  contracts: {
    title: 'Contracts Lifecycle (CON-7)',
    externals: [
      { label: 'India Indian Contract Act 1872', url: 'https://www.indiacode.nic.in/handle/123456789/2187', note: 'Statutory contracting framework' },
      { label: 'UK SoGA 1979', url: 'https://www.legislation.gov.uk/ukpga/1979/54', note: 'Sale of Goods Act (UK)' },
      { label: 'UNCITRAL Model Law on Procurement', url: 'https://uncitral.un.org/en/texts/procurement', note: 'Model procurement contract clauses' },
      { label: 'FIDIC Standard Forms', url: 'https://fidic.org/bookshop/standard-forms-of-contract', note: 'Construction/engineering contract templates' },
    ],
    internals: [
      { label: 'Purchase Orders', path: '/app/po', why: 'Contract clauses inherited into POs' },
      { label: 'Change Orders', path: '/app/variations', why: 'Amendments cascade from contract changes' },
      { label: 'Claims & LD', path: '/app/claims', why: 'Liquidated damages triggered by contract breach' },
      { label: 'Sourcing Award', path: '/app/sourcing/award-recommendation', why: 'Contract initiation begins from award' },
    ],
    authoring: {
      where: 'Contract Repository · New Contract Wizard',
      path: '/app/cm/repository/new',
      action: 'Create new contract',
      note: 'Contracts are authored here (Wizard) or auto-created from Sourcing Award via /api/contracts/v2/from-sourcing-award.',
    },
  },
  claims: {
    title: 'Claims & Liquidated Damages',
    externals: [
      { label: 'FIDIC Yellow Book — Claims', url: 'https://fidic.org', note: 'Standard for contractor claim procedures' },
      { label: 'ICC Force Majeure Clause 2020', url: 'https://iccwbo.org/business-solutions/dispute-resolution-services/icc-force-majeure-and-hardship-clauses/', note: 'Authoritative FM clause' },
      { label: 'UNIDROIT Principles', url: 'https://www.unidroit.org/instruments/commercial-contracts/unidroit-principles-2016/', note: 'Hardship & FM principles' },
    ],
    internals: [
      { label: 'Contracts', path: '/app/contracts', why: 'Claims trace to specific contract clauses' },
      { label: 'Change Orders', path: '/app/variations', why: 'FM declarations may trigger amendments' },
      { label: 'Performance Mgmt', path: '/app/srm/performance', why: 'Claim history feeds supplier scorecards' },
    ],
    authoring: {
      where: 'Claim Initiation Wizard (this page) — PM / Contracts Engineer',
      path: '/app/cld/manual-initiation',
      action: 'Start a new claim',
      note: 'Claims authored here. Stored in `claims`. Force Majeure declared with type=FORCE_MAJEURE. LD claims auto-spawned by cron when contract milestones breach.',
    },
  },

  /* ═══════════════════════ CLAIMS & LD (Iter-162 — per-page granularity) ═══════════════════════ */
  'cld-register': {
    title: 'Claims & LD Register (1.0)',
    externals: [
      { label: 'FIDIC Yellow Book — Claims', url: 'https://fidic.org', note: 'Standard claim procedure' },
      { label: 'ICC Arbitration Rules', url: 'https://iccwbo.org/business-solutions/arbitration/', note: 'Dispute resolution rules' },
      { label: 'NHAI Dispute Adjudication Board', url: 'https://nhai.gov.in', note: 'Public-sector adjudication norms' },
    ],
    internals: [
      { label: 'Contracts Portfolio', path: '/app/cm/portfolio', why: 'Every claim links back to a contract' },
      { label: 'Predictive Risk', path: '/app/cld/predictive-risk', why: 'High-risk contracts surface here as claim candidates' },
      { label: 'Portfolio Analytics', path: '/app/cld/portfolio-analytics', why: 'Boardroom rollup of this register' },
      { label: 'CXO Dashboard', path: '/app/cxo-dashboard', why: 'Hero-level claims exposure feeds CXO view' },
    ],
    authoring: {
      where: 'Claim Initiation Wizard or auto-spawn by LD cron',
      path: '/app/cld/manual-initiation',
      action: 'Open the wizard to start a new claim',
      note: 'Records on this page are read-only summaries of every claim in `claims` + every LD in `ldLedger`. Author new claims via the wizard; LD claims auto-spawn from breach-cron.',
    },
  },
  'cld-claim-initiation': {
    title: 'Claim Initiation Wizard (1.1)',
    externals: [
      { label: 'FIDIC 20.1 — Contractor Claims', url: 'https://fidic.org', note: 'Time-bar + substantiation procedure' },
      { label: 'Indian Contract Act 1872', url: 'https://www.indiacode.nic.in/handle/123456789/2187', note: 'Section 73-75 on breach & damages' },
    ],
    internals: [
      { label: 'Contracts', path: '/app/cm/repository', why: 'Pick the parent contract for the claim' },
      { label: 'Evidence', path: '/app/cld/evidence-v2', why: 'Attach substantiation evidence after initiation' },
      { label: 'Quantum Workbench', path: '/app/cld/quantum-workbench', why: 'Next step after initiation' },
    ],
    authoring: {
      where: 'This page (the wizard)',
      path: '/app/cld/manual-initiation',
      action: 'Submit the wizard',
      note: 'Claims authored exclusively here. Stored in `claims`. The wizard pre-fills counterparty + clause references from the linked contract.',
    },
  },
  'cld-workbench': {
    title: 'CLD v2 Workbench — All-in-One (1.2)',
    externals: [
      { label: 'AAA Construction Industry Rules', url: 'https://www.adr.org', note: 'Construction dispute procedures' },
      { label: 'Society of Construction Law — Delay & Disruption Protocol', url: 'https://www.scl.org.uk', note: 'Industry-standard delay analysis methods' },
    ],
    internals: [
      { label: 'Quantum Workbench', path: '/app/cld/quantum-workbench', why: 'Deep-dive into a single claim quantum' },
      { label: 'Negotiation Table', path: '/app/cld/negotiation-table', why: 'Settlement modelling' },
      { label: 'Adjudication List', path: '/app/cld/adjudication-list', why: 'Escalated disputes' },
    ],
    authoring: {
      where: 'Wizard inside this workbench (Register tab)',
      path: '/app/cld/v2',
      action: 'Start wizard or pick existing claim',
      note: '8 tabs in one place — Register / Merit / Quantum / Monte Carlo / Negotiation / Settlement / Adjudication / Recovery. All tabs work on the same claim picked at top.',
    },
  },
  'cld-portfolio-analytics': {
    title: 'Portfolio Analytics (1.3)',
    externals: [
      { label: 'McKinsey Capital Projects Insights', url: 'https://www.mckinsey.com/industries/capital-projects-and-infrastructure', note: 'Benchmarks on claim ratios' },
      { label: 'KPMG Construction Disputes Survey', url: 'https://kpmg.com', note: 'Annual disputes benchmark' },
    ],
    internals: [
      { label: 'Claims & LD Register', path: '/app/cld/register', why: 'This page summarises that register' },
      { label: 'Predictive Risk', path: '/app/cld/predictive-risk', why: 'Forward-looking complement' },
      { label: 'CXO Dashboard', path: '/app/cxo-dashboard', why: 'Boardroom roll-up' },
    ],
    authoring: {
      where: 'Read-only — computed nightly from `claims` + `ldLedger` + `recoveryLedger`',
      path: '/app/cld/register',
      action: 'Drill into the register to change source data',
      note: 'Heatmaps + boardroom PDF (via reportlab) generated on demand. Click "Export Boardroom PDF" for a print-ready exec brief.',
    },
  },
  'cld-predictive-risk': {
    title: 'Predictive Dispute Risk (1.4)',
    externals: [
      { label: 'Aon Construction Risk Benchmark', url: 'https://www.aon.com', note: 'Construction-sector claim/dispute trends' },
      { label: 'Marsh Risk Index', url: 'https://www.marsh.com', note: 'Insurance-grade dispute scoring' },
    ],
    internals: [
      { label: 'Contracts Health', path: '/app/cm/performance', why: 'Health band feeds the risk score' },
      { label: 'Predictive heatmap', path: '/app/cld/portfolio-analytics', why: 'Visual roll-up of this score' },
    ],
    authoring: {
      where: 'Read-only — produced by a daily forecast cron',
      path: '/app/cm/repository',
      action: 'Update contract data → next cron refreshes the score',
      note: 'Score = f(health, claim density, LD velocity, evidence weakness, adjudication win/loss). 0-100 scale; > 70 flagged RED.',
    },
  },
  'cld-fm-notice': {
    title: 'Force Majeure Notice Hub (2.0)',
    externals: [
      { label: 'ICC FM 2020 Clause', url: 'https://iccwbo.org/business-solutions/dispute-resolution-services/icc-force-majeure-and-hardship-clauses/', note: 'Reference clause text' },
      { label: 'IMD Weather Records', url: 'https://mausam.imd.gov.in', note: 'Authoritative IN weather data for FM' },
      { label: 'WHO Pandemic Alerts', url: 'https://www.who.int/emergencies/disease-outbreak-news', note: 'Pandemic-FM substantiation' },
    ],
    internals: [
      { label: 'Evidence Repository', path: '/app/cld/evidence-v2', why: 'Attach FM substantiation evidence' },
      { label: 'Contracts (FM clause)', path: '/app/cm/clause-library', why: 'Live clause text referenced by AI validator' },
      { label: 'Quantum Workbench', path: '/app/cld/quantum-workbench', why: 'Quantify FM impact' },
    ],
    authoring: {
      where: 'Supplier Portal (declaration) + Buyer console (acceptance / rejection)',
      path: '/app/portal-v2',
      action: 'Supplier declares FM → Buyer validates here',
      note: '6-state FSM: DECLARED → SUBSTANTIATED → VALIDATED → ACCEPTED / REJECTED / WITHDRAWN. AI validator (Cognara) runs at SUBSTANTIATED; aiSource tagged on every transition.',
    },
  },
  'cld-evidence': {
    title: 'Evidence Repository (3.0)',
    externals: [
      { label: 'NIST Digital Forensics Guide', url: 'https://csrc.nist.gov/publications/detail/sp/800-86/final', note: 'Chain-of-custody standards' },
      { label: 'ISO 27037 — Digital Evidence', url: 'https://www.iso.org/standard/44381.html', note: 'Identification, collection & preservation' },
    ],
    internals: [
      { label: 'FM Notice', path: '/app/cld/fm-notice-v2', why: 'FM substantiation requires this evidence' },
      { label: 'Quantum Workbench', path: '/app/cld/quantum-workbench', why: 'Cost-head proof attaches here' },
      { label: 'Adjudication Case Rooms', path: '/app/cld/adjudication-list', why: 'Filings draw from this repository' },
    ],
    authoring: {
      where: 'This page — drag-and-drop upload',
      path: '/app/cld/evidence-v2',
      action: 'Upload + classify',
      note: 'Files stream via auth-gated `/api/files/cld_evidence/*`. SHA-256 fingerprint + AI classification (Cognara) tags `aiClassification.aiSource`.',
    },
  },
  'cld-quantum': {
    title: 'Quantum Assessment (4.0)',
    externals: [
      { label: 'AACE International — Cost Estimating', url: 'https://web.aacei.org', note: 'Cost-engineering recommended practices' },
      { label: 'SCL Delay & Disruption Protocol', url: 'https://www.scl.org.uk', note: 'Methodology selection guidance' },
    ],
    internals: [
      { label: 'Claim Register', path: '/app/cld/register', why: 'Pick the claim to assess' },
      { label: 'Evidence Repository', path: '/app/cld/evidence-v2', why: 'Cost-head proofs' },
      { label: 'Negotiation Table', path: '/app/cld/negotiation-table', why: 'Use P-bands in counter-offers' },
    ],
    authoring: {
      where: 'This page — manual entry per cost-head + AI-suggested methodology',
      path: '/app/cld/quantum-workbench',
      action: 'Pick methodology → fill cost heads → run Monte Carlo',
      note: '7 methodologies supported. AI runs Monte Carlo for > ₹5 Cr claims; aiSource tagged on every assessment row.',
    },
  },
  'cld-ld-clause-library': {
    title: 'LD Clause Library (5.0)',
    externals: [
      { label: 'FIDIC Sub-Clause 8.7 — Delay Damages', url: 'https://fidic.org', note: 'Standard LD clause' },
      { label: 'Hadley v Baxendale', url: 'https://en.wikipedia.org/wiki/Hadley_v_Baxendale', note: 'English law foundation on remoteness' },
      { label: 'Damages for breach — Indian Contract Act §74', url: 'https://www.indiacode.nic.in/handle/123456789/2187', note: 'IN statutory LD framework' },
    ],
    internals: [
      { label: 'LD Config (per-contract)', path: '/app/cld/ld-config', why: 'Apply clauses to specific contracts' },
      { label: 'LD Notice Dispatch', path: '/app/cld/ld-notice', why: 'Generated from clause-library entries' },
      { label: 'Contracts Templates', path: '/app/cm/templates', why: 'Templates embed reference LD clauses' },
    ],
    authoring: {
      where: 'This page — CRUD on canonical LD clauses',
      path: '/app/cld/ld-clause-library',
      action: 'Add / edit / version an LD clause',
      note: 'Clauses authored here. Stored in `ldClauseLibrary`. Each clause carries: rate, cap, basis (daily/weekly), grace period, escalation, jurisdiction.',
    },
  },
  'cld-ld-config': {
    title: 'LD Configuration per Contract (5.1)',
    externals: [
      { label: 'FIDIC App. to GC — Delay Damages', url: 'https://fidic.org', note: 'Particular conditions for LD rate & cap' },
    ],
    internals: [
      { label: 'LD Clause Library', path: '/app/cld/ld-clause-library', why: 'Source of the clause being applied' },
      { label: 'Contract Repository', path: '/app/cm/repository', why: 'Apply LD to a specific contract' },
      { label: 'Milestones', path: '/app/cm/performance', why: 'LD breaches anchor to milestones' },
    ],
    authoring: {
      where: 'This page — link a library clause to a contract',
      path: '/app/cld/ld-config',
      action: 'Apply clause + set parameters',
      note: 'Stored in `liquidatedDamages` + `ldLedger`. Nightly accrual cron evaluates milestone slip and computes LD.',
    },
  },
  'cld-ld-notice': {
    title: 'LD Notice Dispatch (5.2)',
    externals: [
      { label: 'Lloyd\'s Standard Form of Salvage Agreement', url: 'https://www.lloyds.com', note: 'Notice-of-claim formality precedent' },
    ],
    internals: [
      { label: 'LD Config', path: '/app/cld/ld-config', why: 'Source of the rate + cap' },
      { label: 'Recovery Ledger', path: '/app/cld/recovery-ledger', why: 'Acknowledged notices flow into recovery' },
      { label: 'Supplier Portal V2', path: '/app/portal-v2', why: 'Suppliers acknowledge notices here' },
    ],
    authoring: {
      where: 'This page — dispatch notice + bulk acknowledge',
      path: '/app/cld/ld-notice',
      action: 'Dispatch / bulk-acknowledge',
      note: 'Notice text drafted via AI (Cognara) or template fallback; aiSource tagged. Bulk-acknowledge updates `acknowledged: true` + emits event.',
    },
  },
  'cld-negotiation': {
    title: 'Negotiation Table (6.0)',
    externals: [
      { label: 'Harvard PON — Negotiation Frameworks', url: 'https://www.pon.harvard.edu', note: 'BATNA / ZOPA / anchoring methodology' },
    ],
    internals: [
      { label: 'Quantum Workbench', path: '/app/cld/quantum-workbench', why: 'P-bands anchor counter-offers' },
      { label: 'Enforcement / Settlement', path: '/app/cld/enforcement-board', why: 'Approved settlement scenarios flow here' },
    ],
    authoring: {
      where: 'This page — multi-round counter-offer ledger',
      path: '/app/cld/negotiation-table',
      action: 'Add round + justification',
      note: 'Each round timestamped + locked once formally offered. AI advisor runs gap-to-P50 analysis.',
    },
  },
  'cld-enforcement': {
    title: 'Settlement & Enforcement Board (6.1)',
    externals: [
      { label: 'NYC Convention (Arbitral Awards)', url: 'https://www.newyorkconvention.org', note: 'Cross-border enforcement framework' },
    ],
    internals: [
      { label: 'Negotiation Table', path: '/app/cld/negotiation-table', why: 'Negotiated agreements settle here' },
      { label: 'Recovery Ledger', path: '/app/cld/recovery-ledger', why: 'Settled values flow to recovery' },
    ],
    authoring: {
      where: 'This page — pick scenario + generate cert',
      path: '/app/cld/enforcement-board',
      action: 'Approve scenario → generate cert → e-sign',
      note: '3 scenarios (Best/Likely/Worst). E-sign via internal SHA-256 provider; recovery row auto-created on approval.',
    },
  },
  'cld-adjudication': {
    title: 'Adjudication & Dispute Board (7.0)',
    externals: [
      { label: 'CIArb Adjudication Rules', url: 'https://www.ciarb.org', note: 'International adjudication standard' },
      { label: 'UNCITRAL Model Law on Arbitration', url: 'https://uncitral.un.org/en/texts/arbitration/modellaw/commercial_arbitration', note: 'Cross-border arbitration framework' },
    ],
    internals: [
      { label: 'Adjudication Case Rooms', path: '/app/cld/adjudication-list', why: 'Open case rooms per active dispute' },
      { label: 'Evidence Repository', path: '/app/cld/evidence-v2', why: 'Case filings reference repository entries' },
      { label: 'Negotiation Table', path: '/app/cld/negotiation-table', why: 'Pre-adjudication context' },
    ],
    authoring: {
      where: 'This page — refer a disputed claim',
      path: '/app/cld/adjudication-list',
      action: 'Open case → upload filings → record decision',
      note: 'Stored in `adjudicationCases`. Case Counsel agent prepares filings; decisions flow back to parent claim.',
    },
  },
  'cld-adjudication-case': {
    title: 'Adjudication Case Room (7.1)',
    externals: [
      { label: 'CIArb Online Hearing Guidance', url: 'https://www.ciarb.org', note: 'Best practices for virtual hearings' },
    ],
    internals: [
      { label: 'Adjudication List', path: '/app/cld/adjudication-list', why: 'Parent list of all case rooms' },
      { label: 'Evidence Repository', path: '/app/cld/evidence-v2', why: 'Filings can cross-reference repository entries' },
    ],
    authoring: {
      where: 'Inside the case room — upload filings + log hearings',
      path: '/app/cld/adjudication-list',
      action: 'Open the case + add filing',
      note: 'Files via `/api/files/cld_case_documents/*` (auth-gated). Hearing log + decision-of-record stored on the case doc.',
    },
  },
  'cld-recovery': {
    title: 'Recovery Ledger (8.0)',
    externals: [
      { label: 'Set-off doctrine — Common law overview', url: 'https://en.wikipedia.org/wiki/Set-off_(law)', note: 'Legal foundation for invoice deduction' },
    ],
    internals: [
      { label: 'Invoices & Payments', path: '/app/invoices', why: 'Deductions scheduled against payables' },
      { label: 'Settlement Board', path: '/app/cld/enforcement-board', why: 'Settled values feed this ledger' },
      { label: 'LD Notice Dispatch', path: '/app/cld/ld-notice', why: 'Capped LDs flow here' },
    ],
    authoring: {
      where: 'Auto-populated from settlements + acknowledged LD notices',
      path: '/app/cld/enforcement-board',
      action: 'Approve settlement (auto-creates ledger row)',
      note: 'Stored in `recoveryLedger`. Daily aging cron emits 30/60/90-day escalation events. Write-off requires CFO approval.',
    },
  },

  'change-orders': {
    title: 'Change Orders & Amendments',
    externals: [
      { label: 'FIDIC Variations Procedure', url: 'https://fidic.org', note: 'Standard variation/change procedure' },
      { label: 'AIA Standard Change Order', url: 'https://www.aiacontracts.com', note: 'Construction change orders' },
    ],
    internals: [
      { label: 'Contracts', path: '/app/contracts', why: 'Amendments persist against parent contract' },
      { label: 'Purchase Orders', path: '/app/po', why: 'PO change-orders sync with contract amendments' },
      { label: 'Claims & LD', path: '/app/claims', why: 'Linked claims may justify the change' },
    ],
    authoring: {
      where: 'Change Order / Variation Wizard',
      path: '/app/cm/change-order-wizard',
      action: 'Author change order',
      note: 'Change Orders authored here. Stored in `contractAmendments`. Replaces legacy `variations` collection (consolidated Iter-146).',
    },
  },

  // ═══════════════════════ SOURCING (additional sub-modules) ═══════════════════════
  'sourcing-events': {
    title: 'Sourcing Events & Wizard',
    externals: [
      { label: 'TED Tenders Electronic Daily', url: 'https://ted.europa.eu', note: 'EU public tenders database' },
      { label: 'CIPS Procurement Cycle', url: 'https://www.cips.org/knowledge/procurement-topics-and-skills/', note: 'Procurement methodology' },
      { label: 'India GeM Portal', url: 'https://gem.gov.in', note: 'Indian gov-e-marketplace' },
    ],
    internals: [
      { label: 'Strategy & Planning', path: '/app/sourcing/lifecycle', why: 'Approved strategy spawns events' },
      { label: 'Supplier Invitation', path: '/app/sourcing/supplier-invitation', why: 'Long-list → invite for each event' },
      { label: 'Award Recommendation', path: '/app/sourcing/award-recommendation', why: 'Events conclude with award' },
    ],
    authoring: {
      where: 'Sourcing Wizard (intake) — Sourcing Manager',
      path: '/app/sourcing/wizard',
      action: 'Create new event',
      note: 'Sourcing events authored here. Stored in `sourcing_events`. RFI/RFP/RFQ/AUCTION/BAFO/FRAMEWORK/DA all flow through this wizard.',
    },
  },
  evaluation: {
    title: 'Technical & Commercial Evaluation',
    externals: [
      { label: 'PMBOK Procurement', url: 'https://www.pmi.org/pmbok-guide-standards', note: 'PMI evaluation methods' },
      { label: 'CIPS — QCBS Method', url: 'https://www.cips.org/knowledge/', note: 'Quality-cost-based selection' },
    ],
    internals: [
      { label: 'Bid Submission', path: '/app/sourcing/bid-submission', why: 'Bids feed evaluation' },
      { label: 'Bid Opening', path: '/app/sourcing/bid-opening', why: 'Opened bids enter eval workspace' },
      { label: 'Award Recommendation', path: '/app/sourcing/award-recommendation', why: 'Evaluation outputs trigger award' },
    ],
    authoring: {
      where: 'Evaluation Workspace (this page) — Evaluator panel',
      path: '/app/sourcing/evaluation',
      action: 'Score bid / add note',
      note: 'Evaluator scores + comments authored here. Stored in `bid_evaluations`. QCBS / L1 / weighted formulas computed server-side.',
    },
  },
  auction: {
    title: 'eAuction Execution',
    externals: [
      { label: 'CIPS Reverse Auctions Guide', url: 'https://www.cips.org/knowledge/', note: 'Best-practice auction design' },
      { label: 'Spend Matters Auction Tech', url: 'https://spendmatters.com', note: 'Industry analyst commentary' },
    ],
    internals: [
      { label: 'Bid Submission', path: '/app/sourcing/bid-submission', why: 'Eligible bids progress to auction' },
      { label: 'BAFO', path: '/app/sourcing/bafo', why: 'BAFO is the alternative to live auction' },
      { label: 'Award Recommendation', path: '/app/sourcing/award-recommendation', why: 'Auction winner drives award' },
    ],
    authoring: {
      where: 'Supplier Portal (live bidding) + Auction Cockpit (admin view)',
      path: '/app/sourcing/auction',
      action: 'Open Auction Cockpit',
      note: 'Bid values authored by suppliers on Portal during the live window (`/portal/auction/{eventId}`). Each tick stored in `auction_bids`. Reverse/Dutch/Sealed/Hybrid supported.',
    },
  },
  bafo: {
    title: 'Best & Final Offer (BAFO)',
    externals: [
      { label: 'US FAR — BAFO Procedures', url: 'https://www.acquisition.gov/far/', note: 'US federal BAFO rules' },
      { label: 'CIPS BAFO Guidance', url: 'https://www.cips.org/knowledge/', note: 'BAFO methodology' },
    ],
    internals: [
      { label: 'Evaluation', path: '/app/sourcing/evaluation', why: 'Shortlist from eval invited to BAFO' },
      { label: 'Award Recommendation', path: '/app/sourcing/award-recommendation', why: 'BAFO outcome drives award' },
    ],
    authoring: {
      where: 'Supplier Portal (BAFO envelope) + BAFO admin',
      path: '/app/sourcing/bafo',
      action: 'Open BAFO event',
      note: 'BAFO offers authored by shortlisted suppliers on Portal (`/portal/bafo/{bafoEventId}`). Stored in `bafo_offers`. Multi-round supported.',
    },
  },
  framework: {
    title: 'Framework Agreement Management',
    externals: [
      { label: 'UK CCS Frameworks', url: 'https://www.crowncommercial.gov.uk/agreements', note: 'UK public frameworks library' },
      { label: 'EU Framework Procedures', url: 'https://eur-lex.europa.eu', note: 'EU framework legal basis' },
    ],
    internals: [
      { label: 'Award Recommendation', path: '/app/sourcing/award-recommendation', why: 'Frameworks come from awarded events' },
      { label: 'Purchase Orders', path: '/app/po', why: 'Call-off POs draw on framework caps' },
      { label: 'Contracts', path: '/app/contracts', why: 'Frameworks materialise as parent contracts' },
    ],
    authoring: {
      where: 'Framework Agreement Builder (this page) — Sourcing Manager',
      path: '/app/sourcing/framework-agreements',
      action: 'Configure framework lots',
      note: 'Frameworks authored here from awarded sourcing events. Cascading / Rotation / Mini-Competition rules configured here. Stored in `frameworks` with call-off POs in `purchase_orders`.',
    },
  },
  'direct-award': {
    title: 'Direct Award Management',
    externals: [
      { label: 'EU 2014/24/EU Article 32', url: 'https://eur-lex.europa.eu', note: 'EU rules on negotiated procedure w/o publication' },
      { label: 'India GFR Rule 154', url: 'https://doe.gov.in', note: 'India direct procurement rules' },
      { label: 'US FAR Subpart 6.3', url: 'https://www.acquisition.gov/far/subpart-6.3', note: 'US sole-source justification' },
    ],
    internals: [
      { label: 'Award Recommendation', path: '/app/sourcing/award-recommendation', why: 'DA flows through approval/closure' },
      { label: 'ITT Compliance', path: '/app/sourcing/itt-compliance', why: 'DA requires statutory justification' },
      { label: 'Contracts', path: '/app/contracts', why: 'DA awards initiate contracts' },
    ],
    authoring: {
      where: 'Direct Award Wizard (this page) — Sourcing Manager + Compliance Officer',
      path: '/app/sourcing/direct-award',
      action: 'Start direct award case',
      note: 'DA case authored here with statutory justification (GFR 154 / FAR 6.3 / EU Art 32). Stored in `direct_awards`. Approval routed via DOA matrix.',
    },
  },
  'award-recommendation': {
    title: 'Award Recommendation, Approval & Closure (SS-4.11/4.12)',
    externals: [
      { label: 'EU Standstill (Remedies Directive)', url: 'https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:32007L0066', note: 'EU 10-day standstill rules' },
      { label: 'UK PCR 2015 — Standstill', url: 'https://www.legislation.gov.uk/uksi/2015/102/contents/made', note: 'UK standstill periods' },
      { label: 'TED Award Notices', url: 'https://op.europa.eu/en/web/eforms', note: 'EU award notice eForms' },
      { label: 'India CVC Procurement Vigilance', url: 'https://cvc.gov.in', note: 'Indian award oversight' },
    ],
    internals: [
      { label: 'Evaluation', path: '/app/sourcing/evaluation', why: 'Eval outputs prepopulate the award recommendation' },
      { label: 'BAFO / Auction', path: '/app/sourcing/bafo', why: 'BAFO/auction winners drive award' },
      { label: 'Communication Engine', path: '/app/sourcing/communication-engine', why: 'Award + regret letters fan out from here' },
      { label: 'Contracts', path: '/app/contracts', why: 'Closure initiates the contract' },
      { label: 'ITT Compliance', path: '/app/sourcing/itt-compliance', why: 'Standstill clock starts on approval' },
    ],
    authoring: {
      where: 'Award Recommendation (this page) — Sourcing Manager',
      path: '/app/sourcing/award-recommendation',
      action: 'Draft recommendation',
      note: 'Award rec authored here from evaluation output. Standstill clock starts on approval. Stored in `award_recommendations`. Closure auto-creates a DRAFT contract.',
    },
  },
  'sourcing-cockpit': {
    title: 'Sourcing Cockpit & Calendar',
    externals: [
      { label: 'CIPS Procurement Calendar', url: 'https://www.cips.org', note: 'Procurement planning cadence' },
    ],
    internals: [
      { label: 'Sourcing Events', path: '/app/sourcing/events', why: 'All events surface on the cockpit' },
      { label: 'Sourcing Analytics', path: '/app/sourcing/analytics', why: 'Cockpit KPIs feed analytics' },
    ],
    authoring: {
      where: 'Read-only — aggregated from all Sourcing Events',
      path: '/app/sourcing/events',
      action: 'Open events list',
      note: 'Cockpit + Calendar are read-only roll-ups. Authoring of events happens in the Sourcing Wizard.',
    },
  },

  // ═══════════════════════ ADMIN, CATALOGUE, P2P ═══════════════════════
  catalogue: {
    title: 'Catalogue & Punchout',
    externals: [
      { label: 'cXML / Ariba Punchout Spec', url: 'http://xml.cxml.org', note: 'Ariba punchout standard' },
      { label: 'OAGI Open Catalog Format', url: 'https://oagi.org', note: 'Open Applications Group format' },
    ],
    internals: [
      { label: 'Requisitions', path: '/app/requisitions', why: 'Catalogue items auto-approve as PR lines' },
      { label: 'Frameworks', path: '/app/sourcing/framework-agreements', why: 'Framework items can publish to catalogue' },
    ],
    authoring: {
      where: 'Catalogue Admin (internal items) · Supplier Portal (punchout SKUs)',
      path: '/app/catalogue/internal',
      action: 'Add / sync catalogue item',
      note: 'Internal catalogue items authored by Category Manager. Punchout items synced from supplier sites (cXML). Stored in `catalogue_items`.',
    },
  },
  p2p: null,  // alias resolved below
};

// ═══════════════════════ CONTRACTS LIFECYCLE — Sub-modules (CM-7.x) ═══════════════════════
// Every cm/* sub-module from CONTRACTS_MODULES gets a dedicated Quick Links
// entry with externals, internals AND an `authoring` block telling the user
// where the data on this page is actually authored (the "source of truth").
const _CM_EXT = {
  fidic: { label: 'FIDIC Standard Forms', url: 'https://fidic.org/bookshop/standard-forms-of-contract', note: 'Construction/engineering contract templates' },
  uncitral: { label: 'UNCITRAL Model Law', url: 'https://uncitral.un.org/en/texts/procurement', note: 'Model procurement contract clauses' },
  ica1872: { label: 'India Contract Act 1872', url: 'https://www.indiacode.nic.in/handle/123456789/2187', note: 'Statutory contracting framework' },
  iccfm: { label: 'ICC Force Majeure 2020', url: 'https://iccwbo.org/business-solutions/dispute-resolution-services/icc-force-majeure-and-hardship-clauses/', note: 'Authoritative FM clause' },
  iccarb: { label: 'ICC Arbitration Rules', url: 'https://iccwbo.org/dispute-resolution-services/arbitration/rules-of-arbitration/', note: 'ICC arbitration procedure' },
  aia: { label: 'AIA Standard Forms', url: 'https://www.aiacontracts.com', note: 'AIA construction contract documents' },
  unidroit: { label: 'UNIDROIT Principles', url: 'https://www.unidroit.org/instruments/commercial-contracts/unidroit-principles-2016/', note: 'Hardship & FM principles' },
  ias16: { label: 'IAS 16 / Ind AS 16', url: 'https://www.mca.gov.in/Ministry/pdf/Ind%20AS%2016.pdf', note: 'PP&E accounting for capitalisation of contract assets' },
  irda: { label: 'IRDAI India', url: 'https://irdai.gov.in', note: 'Insurance regulator — policy renewal compliance' },
  rbiBG: { label: 'RBI Bank Guarantee Guidelines', url: 'https://www.rbi.org.in', note: 'Bank guarantee / performance bond rules' },
  stamp: { label: 'India Stamp Act', url: 'https://www.indiacode.nic.in/handle/123456789/2192', note: 'Contract stamping / execution' },
  eidasESign: { label: 'eIDAS Regulation (EU)', url: 'https://digital-strategy.ec.europa.eu/en/policies/eidas-regulation', note: 'EU e-signature legal basis' },
  itaIN: { label: 'India IT Act 2000 §3A', url: 'https://www.meity.gov.in/content/information-technology-act-2000', note: 'Electronic signature validity in India' },
};

const CM_LINKS = {
  // ────────── Contract Types & Repository ──────────
  'cm-contract-types': {
    title: 'Supported Contract Types',
    externals: [_CM_EXT.fidic, _CM_EXT.aia, _CM_EXT.uncitral,
      { label: 'NEC4 Contracts', url: 'https://www.neccontract.com/products/nec4-contracts', note: 'NEC4 family of construction contracts' }],
    internals: [
      { label: 'Contract Repository', path: '/app/cm/repository', why: 'Browse contracts by type' },
      { label: 'Templates', path: '/app/cm/templates', why: 'Each type maps to a template' },
      { label: 'Clause Library', path: '/app/cm/clause-library', why: 'Type-specific clause picker' },
    ],
    authoring: {
      where: 'System Config → Contract Types (Admin) · seeded list',
      path: '/app/admin/system-config',
      action: 'Manage contract types',
      note: 'This is a reference page. The type taxonomy itself is seeded in /app/backend/services/clm_setup_seed.py and editable by admins.',
    },
  },
  'cm-repository': {
    title: 'Contract Repository Architecture',
    externals: [_CM_EXT.ica1872, _CM_EXT.fidic, _CM_EXT.uncitral],
    internals: [
      { label: 'Portfolio Dashboard', path: '/app/cm/portfolio', why: 'Cross-portfolio analytics view' },
      { label: 'Integration Hub', path: '/app/cm/integrations', why: 'Per-contract supplier 360 / PR-PO-Invoice' },
      { label: 'Sourcing Award', path: '/app/sourcing/award-recommendation', why: 'Award closure flows into the repository' },
    ],
    authoring: {
      where: 'New Contract Wizard (intake) or Sourcing Award handoff',
      path: '/app/cm/repository/new',
      action: 'New contract',
      note: 'Contracts created here become the single source of truth. Award-handoff also writes into this repository via POST /api/contracts/v2/from-sourcing-award.',
    },
  },
  'cm-portfolio': {
    title: 'Portfolio Dashboard (Renewals · Bonds · Insurance)',
    externals: [_CM_EXT.rbiBG, _CM_EXT.irda],
    internals: [
      { label: 'Bonds & BG', path: '/app/cm/bonds', why: 'Bond expiry pipeline' },
      { label: 'Insurance Calendar', path: '/app/cm/insurance', why: 'Policy renewal calendar' },
      { label: 'Renewal AI', path: '/app/cm/renewal', why: 'Renewal action queue' },
    ],
    authoring: {
      where: 'Read-only — aggregated from Contracts, Bonds, Insurance, Renewals',
      path: '/app/cm/repository',
      action: 'Browse contracts',
      note: 'Portfolio = read-only roll-up. Author data in Contract Wizard (contracts) or in Bonds / Insurance pages.',
    },
  },
  'cm-bonds': {
    title: 'Bonds & Bank Guarantees',
    externals: [_CM_EXT.rbiBG,
      { label: 'URDG 758 (ICC)', url: 'https://iccwbo.org/business-solutions/banking-finance/global-rules/urdg-758/', note: 'Uniform Rules for Demand Guarantees' }],
    internals: [
      { label: 'Portfolio Dashboard', path: '/app/cm/portfolio', why: 'Bond expiry pipeline view' },
      { label: 'Contract Repository', path: '/app/cm/repository', why: 'Bonds attach to parent contract' },
    ],
    authoring: {
      where: 'Contract Detail → Bonds tab (per contract)',
      path: '/app/cm/repository',
      action: 'Open contract → Bonds',
      note: 'Bonds are authored against a specific contract under Contract Detail → Bonds. Status auto-updates via SS-4.4 cron (PRACTICAL_COMPLETION → bond release).',
    },
  },
  'cm-insurance': {
    title: 'Insurance Renewal Calendar',
    externals: [_CM_EXT.irda,
      { label: 'GIC Re India', url: 'https://www.gicre.in', note: 'National reinsurance reference' }],
    internals: [
      { label: 'Portfolio Dashboard', path: '/app/cm/portfolio', why: 'Renewals + Bonds + Insurance overview' },
      { label: 'Contract Repository', path: '/app/cm/repository', why: 'Insurance policies link to contracts' },
    ],
    authoring: {
      where: 'Contract Detail → Insurance tab (per contract)',
      path: '/app/cm/repository',
      action: 'Open contract → Insurance',
      note: 'Insurance policies are authored against the parent contract in Contract Detail → Insurance. The calendar here is a read-only roll-up.',
    },
  },
  'cm-integrations': {
    title: 'Integration Hub — Supplier 360 · PR/PO · Spend · Sourcing',
    externals: [
      { label: 'World Bank Open Data', url: 'https://data.worldbank.org', note: 'Macro benchmarks for contract performance' },
    ],
    internals: [
      { label: 'Supplier 360', path: '/app/srm/registration', why: 'Drill-through into supplier master' },
      { label: 'PR / PO / Invoices', path: '/app/p2p', why: 'PR-PO-Invoice rollup feeds this view' },
      { label: 'Spend Analytics', path: '/app/scm/spend-cube', why: 'Spend attribution by contract' },
      { label: 'Sourcing Events', path: '/app/sourcing/events', why: 'Parent + child events linked here' },
    ],
    authoring: {
      where: 'Read-only — joined view across modules',
      path: '/app/cm/repository',
      action: 'Pick a contract',
      note: 'No authoring on this page. Data is sourced from PR (RQ-5), PO (PO-6), Invoices (IP-8), Spend Cube (SC-2), Sourcing (SS-4), and Supplier Master (SR-1).',
    },
  },

  // ────────── Contract Request & Initiation ──────────
  'cm-request-form': {
    title: 'Contract Request Form',
    externals: [_CM_EXT.uncitral, _CM_EXT.ica1872],
    internals: [
      { label: 'Contract Repository', path: '/app/cm/repository', why: 'Approved request → DRAFT contract' },
      { label: 'Risk Assessment', path: '/app/cm/risk-assessment', why: 'Next step after intake' },
      { label: 'Sourcing Award', path: '/app/sourcing/award-recommendation', why: 'Sourcing-led intake bypasses manual request' },
    ],
    authoring: {
      where: 'Contract Wizard (this page) or Sourcing Award handoff',
      path: '/app/cm/repository/new',
      action: 'Start new contract',
      note: 'Manual intake originates here. For sourcing-led flow, the award closure auto-files a DRAFT.',
    },
  },
  'cm-risk-assessment': {
    title: 'Pre-Contract Risk Assessment',
    externals: [_CM_EXT.uncitral,
      { label: 'OFAC SDN List', url: 'https://sanctionssearch.ofac.treas.gov', note: 'Sanctions screening of counterparty' }],
    internals: [
      { label: 'Risk Management', path: '/app/srm/risk', why: 'Supplier-side risk drivers feed the score' },
      { label: 'AI Drafting', path: '/app/cm/ai-drafting', why: 'High-risk contracts route to legal pre-draft' },
    ],
    authoring: {
      where: 'AI risk-scoring engine (Cognara) · per contract on intake',
      path: '/app/cm/repository/new',
      action: 'Run risk-assess from Wizard',
      note: 'Risk score is authored automatically by POST /api/contracts/v2/{id}/risk-assess (Cognara 4-dimension). It is non-editable; overrides go to the audit log.',
    },
  },

  // ────────── AI-Assisted Authoring ──────────
  'cm-clause-library': {
    title: 'Clause Library & Template Management',
    externals: [_CM_EXT.fidic, _CM_EXT.uncitral, _CM_EXT.aia,
      { label: 'IACCM (WorldCC) Clause Library', url: 'https://www.worldcc.com', note: 'Industry-standard clause language' }],
    internals: [
      { label: 'Templates', path: '/app/cm/templates', why: 'Clauses are embedded in templates' },
      { label: 'AI Drafting', path: '/app/cm/ai-drafting', why: 'Drafting consumes the clause library' },
      { label: 'Playbooks', path: '/app/cm/playbooks', why: 'Playbook positions reference clauses' },
    ],
    authoring: {
      where: 'Clause Library page (this page) — Admin/Legal only',
      path: '/app/cm/clause-library',
      action: 'Add / edit clause',
      note: 'Clauses authored here are the master record (collection: `clauses`). Variables are referenced in templates via {{var}} placeholders.',
    },
  },
  'cm-templates': {
    title: 'Contract Templates',
    externals: [_CM_EXT.fidic, _CM_EXT.aia,
      { label: 'NEC4 Contracts', url: 'https://www.neccontract.com/products/nec4-contracts', note: 'NEC4 templates reference' }],
    internals: [
      { label: 'Clause Library', path: '/app/cm/clause-library', why: 'Templates compose from clauses' },
      { label: 'AI Drafting', path: '/app/cm/ai-drafting', why: 'AI uses templates as a base' },
    ],
    authoring: {
      where: 'Template Builder page (this page)',
      path: '/app/cm/templates',
      action: 'Create / edit template',
      note: 'Templates are JSON section trees referencing clause IDs. Stored in `contractTemplates`. Admin role required.',
    },
  },
  'cm-ai-drafting': {
    title: 'AI-Assisted Drafting Engine',
    externals: [_CM_EXT.fidic, _CM_EXT.uncitral],
    internals: [
      { label: 'Templates', path: '/app/cm/templates', why: 'Seed the draft from a chosen template' },
      { label: 'Clause Library', path: '/app/cm/clause-library', why: 'Drafting picks clauses by topic + risk' },
      { label: 'Self-Review', path: '/app/cm/self-review', why: 'Run AI quality checks on the draft' },
    ],
    authoring: {
      where: 'AI Drafting page (Cognara) — per contract',
      path: '/app/cm/ai-drafting',
      action: 'Draft from template + parameters',
      note: 'AI authors the body. POST /api/contracts/v2/{id}/draft-from-template (Cognara via Emergent LLM key). Output stored in `contract.draftBody` with version history.',
    },
  },
  'cm-ai-generation': {
    title: 'Intelligent Contract Generation',
    externals: [_CM_EXT.fidic, _CM_EXT.uncitral],
    internals: [
      { label: 'AI Drafting', path: '/app/cm/ai-drafting', why: 'Underlying drafting engine' },
      { label: 'Risk Assessment', path: '/app/cm/risk-assessment', why: 'Risk-aware clause choice' },
    ],
    authoring: {
      where: 'AI Drafting (Cognara) on save → PDF render endpoint',
      path: '/app/cm/ai-drafting',
      action: 'Generate contract',
      note: 'Generation = drafting + PDF render. PDF authoring lives in /app/backend/services/clm_pdf_render.py.',
    },
  },
  'cm-collaborative-editing': {
    title: 'Collaborative Editing Environment',
    externals: [{ label: 'OpenAPI WebDAV', url: 'http://www.webdav.org', note: 'Versioned doc collab spec' }],
    internals: [
      { label: 'AI Drafting', path: '/app/cm/ai-drafting', why: 'Author seeded here' },
      { label: 'Redline Exchange', path: '/app/cm/redline-exchange', why: 'Counterparty changes flow here' },
    ],
    authoring: {
      where: 'Collaborative Editor + Correspondence threads',
      path: '/app/cm/ai-drafting',
      action: 'Open draft',
      note: 'Authoring lives in `contract.draftBody.versions[]`. Each save = new version with delta + author. Correspondence comments go to `correspondence` collection.',
    },
  },
  'cm-self-review': {
    title: 'Contract Self-Review & Quality Checks',
    externals: [_CM_EXT.fidic, _CM_EXT.uncitral],
    internals: [
      { label: 'AI Drafting', path: '/app/cm/ai-drafting', why: 'Self-review runs on the active draft' },
      { label: 'Internal Review', path: '/app/cm/internal-review', why: 'Next step after self-review' },
    ],
    authoring: {
      where: 'AI Self-Review (Cognara) — read-only findings',
      path: '/app/cm/ai-drafting',
      action: 'Run self-review',
      note: 'Findings authored by /api/contracts/v2/{id}/self-review and stored as `contract.reviewFindings[]` (non-editable; resolution comments allowed).',
    },
  },

  // ────────── Review & Negotiation ──────────
  'cm-internal-review': {
    title: 'Internal Review Workflow',
    externals: [_CM_EXT.uncitral],
    internals: [
      { label: 'Multi-Stakeholder Routing', path: '/app/cm/multi-stakeholder', why: 'DOA-driven stage chaining' },
      { label: 'Legal Review', path: '/app/cm/legal-review', why: 'Legal stage is part of workflow' },
    ],
    authoring: {
      where: 'Admin → Review Workflow Builder',
      path: '/app/admin/workflows',
      action: 'Create / edit workflow',
      note: 'Workflow definitions live in `reviewWorkflows` (admin-authored). Each contract gets an instance via POST /api/contracts/v2/{id}/review/start.',
    },
  },
  'cm-multi-stakeholder': {
    title: 'Multi-Stakeholder Review Routing',
    externals: [_CM_EXT.uncitral],
    internals: [
      { label: 'Internal Review', path: '/app/cm/internal-review', why: 'Parent workflow' },
      { label: 'DOA Matrix', path: '/app/sourcing/doa-matrix', why: 'Authority limits drive routing' },
    ],
    authoring: {
      where: 'DOA Matrix Admin (authority) + Review Workflow Builder (stages)',
      path: '/app/sourcing/doa-matrix',
      action: 'Configure DOA',
      note: 'Routing combines DOA Matrix entries (authority) with Review Workflow stages (sequence). Both are admin-authored.',
    },
  },
  'cm-legal-review': {
    title: 'Legal Review Intelligence',
    externals: [_CM_EXT.fidic, _CM_EXT.uncitral, _CM_EXT.ica1872],
    internals: [
      { label: 'Internal Review', path: '/app/cm/internal-review', why: 'Legal is the last internal stage' },
      { label: 'Clause Library', path: '/app/cm/clause-library', why: 'Findings cite library clauses' },
    ],
    authoring: {
      where: 'AI Legal Review (Cognara) + Legal user comments',
      path: '/app/cm/ai-drafting',
      action: 'Run legal review',
      note: 'AI authors findings; Legal user authors disposition. Stored as `contract.legalFindings[]`. Findings are referenced in audit trail.',
    },
  },
  'cm-negotiation': {
    title: 'Negotiation & Redlines',
    externals: [_CM_EXT.uncitral, _CM_EXT.fidic],
    internals: [
      { label: 'Playbooks', path: '/app/cm/playbooks', why: 'Counter-position library' },
      { label: 'Redline Exchange', path: '/app/cm/redline-exchange', why: 'Counterparty round-tripping' },
      { label: 'E-Signature', path: '/app/cm/esign', why: 'Final agreed text moves to e-sign' },
    ],
    authoring: {
      where: 'Negotiation page — redline diff editor',
      path: '/app/cm/negotiation',
      action: 'Add redline change',
      note: 'Each redline edit creates a row in `redlines` (with rationale, owner, status). Diff engine lives at /app/backend/services/clm_diff_engine.py.',
    },
  },
  'cm-external-negotiation': {
    title: 'External Negotiation Management',
    externals: [_CM_EXT.uncitral],
    internals: [
      { label: 'Negotiation & Redlines', path: '/app/cm/negotiation', why: 'Internal counterpart' },
      { label: 'Redline Exchange', path: '/app/cm/redline-exchange', why: 'Round-trip with counterparty' },
    ],
    authoring: {
      where: 'External Negotiation page — emails + tracked rounds',
      path: '/app/cm/negotiation',
      action: 'Start a negotiation round',
      note: 'Negotiation rounds (`negotiationRounds`) are authored here. Outbound dispatch uses Resend (email). Counterparty replies create new rounds.',
    },
  },
  'cm-redline-exchange': {
    title: 'Counterparty Redline Exchange',
    externals: [{ label: 'OpenAPI Track Changes', url: 'https://learn.microsoft.com/en-us/openspecs/office_standards/ms-doc/', note: 'Track-changes interchange format' }],
    internals: [
      { label: 'Negotiation & Redlines', path: '/app/cm/negotiation', why: 'Internal redline workspace' },
      { label: 'Playbooks', path: '/app/cm/playbooks', why: 'Auto-respond by playbook' },
    ],
    authoring: {
      where: 'Redline Exchange page — accept / reject / counter',
      path: '/app/cm/negotiation',
      action: 'Open active negotiation',
      note: 'Counterparty redlines arrive via Resend → parsed into `redlines` (status=INBOUND). Internal owner authors disposition (ACCEPT / REJECT / COUNTER) here.',
    },
  },
  'cm-playbooks': {
    title: 'Negotiation Playbook Library',
    externals: [{ label: 'WorldCC Playbook Guide', url: 'https://www.worldcc.com', note: 'Industry playbook authoring guide' }],
    internals: [
      { label: 'Clause Library', path: '/app/cm/clause-library', why: 'Playbook positions cite clauses' },
      { label: 'Negotiation & Redlines', path: '/app/cm/negotiation', why: 'Playbooks consumed in negotiation' },
    ],
    authoring: {
      where: 'Playbook Library page — Legal/Admin only',
      path: '/app/cm/playbooks',
      action: 'Create / edit playbook position',
      note: 'Playbook positions (must-have / preferred / walk-away) authored here. Stored in `negotiationPlaybooks`. Linked by clause topic.',
    },
  },
  'cm-esign': {
    title: 'E-Signature Envelopes',
    externals: [_CM_EXT.eidasESign, _CM_EXT.itaIN,
      { label: 'India ESIGN — Aadhaar eSign', url: 'https://cca.gov.in', note: 'India CCA eSign framework' }],
    internals: [
      { label: 'Approval & Signing', path: '/app/cm/approval-signing', why: 'Approval gate before envelope dispatch' },
      { label: 'Contract Repository', path: '/app/cm/repository', why: 'Signed PDFs land in repository' },
    ],
    authoring: {
      where: 'E-Sign page — envelope creator',
      path: '/app/cm/esign',
      action: 'Create envelope',
      note: 'Envelopes (`esignEnvelopes`) authored here. SHA-256 hash + signer log captured. Resend dispatches sign-link emails.',
    },
  },
  'cm-approval-signing': {
    title: 'Contract Approval & Signing',
    externals: [_CM_EXT.stamp, _CM_EXT.eidasESign],
    internals: [
      { label: 'E-Signature', path: '/app/cm/esign', why: 'Approval triggers envelope' },
      { label: 'Internal Review', path: '/app/cm/internal-review', why: 'Approval rides on the workflow' },
    ],
    authoring: {
      where: 'Internal Review Workflow — final approval stage',
      path: '/app/cm/internal-review',
      action: 'Open review stage',
      note: 'Approvers authored in the workflow definition (admin). Approval decision authored here → state machine moves contract to PENDING_SIGNATURE.',
    },
  },

  // ────────── Obligation Management ──────────
  'cm-ai-obligation': {
    title: 'AI Obligation Extraction',
    externals: [_CM_EXT.fidic, _CM_EXT.uncitral],
    internals: [
      { label: 'Obligation Tracking', path: '/app/cm/obligation-tracking', why: 'Extracted obligations land here' },
      { label: 'Obligation Categories', path: '/app/cm/obligation-categories', why: 'Categorisation taxonomy' },
    ],
    authoring: {
      where: 'AI extractor (Cognara + pdfplumber) — run on signed PDF',
      path: '/app/cm/ai-obligation',
      action: 'Trigger extraction',
      note: 'Obligations are authored by POST /api/contracts/v2/{id}/extract-obligations (Cognara). Stored in `obligations`. Owner + due-date manually editable post-extraction.',
    },
  },
  'cm-obligation-categories': {
    title: 'Obligation Categories & Fields',
    externals: [_CM_EXT.fidic],
    internals: [
      { label: 'AI Extraction', path: '/app/cm/ai-obligation', why: 'Extraction uses these categories' },
      { label: 'Obligation Tracking', path: '/app/cm/obligation-tracking', why: 'Categorised obligations tracked here' },
    ],
    authoring: {
      where: 'System Config → Obligation Taxonomy (Admin)',
      path: '/app/admin/system-config',
      action: 'Edit taxonomy',
      note: 'Categories are seeded in /app/backend/services/clm_obligation_taxonomy.py. Admins can extend via System Config.',
    },
  },
  'cm-obligation-tracking': {
    title: 'Obligation Calendar & Tracking',
    externals: [{ label: 'iCalendar RFC 5545', url: 'https://www.rfc-editor.org/rfc/rfc5545', note: 'Calendar invite spec for due-date reminders' }],
    internals: [
      { label: 'AI Extraction', path: '/app/cm/ai-obligation', why: 'Source of tracked obligations' },
      { label: 'Performance Monitoring', path: '/app/cm/performance', why: 'Breach feeds health score' },
      { label: 'Claims & LD', path: '/app/claims', why: 'Breach can trigger LD claim' },
    ],
    authoring: {
      where: 'Per-obligation owner — status updates only',
      path: '/app/cm/obligation-tracking',
      action: 'Update status / evidence',
      note: 'Owner authors status (DONE / IN_PROGRESS / OVERDUE) + uploads evidence. Auto-overdue computed by cron in /app/backend/services/clm_crons.py.',
    },
  },
  'cm-obligations': {
    title: 'Obligations · FM Suspension · Dashboard',
    externals: [_CM_EXT.iccfm, _CM_EXT.unidroit],
    internals: [
      { label: 'Obligation Tracking', path: '/app/cm/obligation-tracking', why: 'Underlying register' },
      { label: 'Claims (FM)', path: '/app/claims', why: 'FM declarations suspend obligations' },
    ],
    authoring: {
      where: 'Obligation Tracking (status) + Claims (FM declaration)',
      path: '/app/cm/obligation-tracking',
      action: 'Manage obligations',
      note: 'This page is read-only roll-up. FM-suspension flag authored in Claims module via FORCE_MAJEURE claim type.',
    },
  },

  // ────────── Performance Monitoring ──────────
  'cm-kpi-framework': {
    title: 'KPI Framework & SLA Tracking',
    externals: [{ label: 'ISO 22301 (BCP)', url: 'https://www.iso.org/standard/75106.html', note: 'SLA & BCP reference for service contracts' }],
    internals: [
      { label: 'Health Score', path: '/app/cm/health-score', why: 'KPIs feed health score' },
      { label: 'Performance Mgmt', path: '/app/srm/performance', why: 'Supplier-side KPI mirror' },
    ],
    authoring: {
      where: 'Contract Detail → KPI Setup (per contract)',
      path: '/app/cm/repository',
      action: 'Open contract → KPI Setup',
      note: 'KPIs are authored per contract in Contract Detail. Stored in `contract.kpis[]`. SLA breaches authored automatically by /api/contracts/v2/{id}/kpi/recompute.',
    },
  },
  'cm-financial-monitoring': {
    title: 'Financial Performance Monitoring',
    externals: [_CM_EXT.ias16,
      { label: 'India Ind AS 11', url: 'https://www.mca.gov.in/Ministry/pdf/Ind%20AS%2011.pdf', note: 'Construction contract revenue recognition' }],
    internals: [
      { label: 'Spend Analytics', path: '/app/scm/spend-cube', why: 'Spend attribution by contract' },
      { label: 'Invoices', path: '/app/invoices', why: 'Certified value comes from invoice posting' },
      { label: 'Change Orders', path: '/app/cm/amendments', why: 'VOs change financial baselines' },
    ],
    authoring: {
      where: 'Read-only — computed from Invoices, Amendments, Original Value',
      path: '/app/cm/repository',
      action: 'Open contract',
      note: 'Numbers are derived. Original value authored in Contract Wizard; VOs in Amendments; certified value in Invoices via 3-way match.',
    },
  },
  'cm-health-score': {
    title: 'Contract Health Score',
    externals: [_CM_EXT.uncitral],
    internals: [
      { label: 'KPI Framework', path: '/app/cm/kpi-framework', why: 'KPI signals feed score' },
      { label: 'Obligation Tracking', path: '/app/cm/obligation-tracking', why: 'Obligation breach is a signal' },
      { label: 'Risk Assessment', path: '/app/cm/risk-assessment', why: 'Initial risk = baseline health' },
    ],
    authoring: {
      where: 'AI health-score engine (Cognara) — recomputed nightly',
      path: '/app/cm/performance',
      action: 'Trigger recompute',
      note: 'Score authored by /api/contracts/v2/{id}/health-score (Cognara). Stored as `contract.healthScore`. Cron in clm_crons.py runs nightly.',
    },
  },
  'cm-performance': {
    title: 'Performance — Health · KPIs · Snapshot',
    externals: [_CM_EXT.uncitral, _CM_EXT.fidic],
    internals: [
      { label: 'KPI Framework', path: '/app/cm/kpi-framework', why: 'KPI definitions' },
      { label: 'Health Score', path: '/app/cm/health-score', why: 'Composite signal' },
      { label: 'Supplier Performance', path: '/app/srm/performance', why: 'Mirror at supplier level' },
    ],
    authoring: {
      where: 'KPI Framework (definitions) · Health Score (composite)',
      path: '/app/cm/kpi-framework',
      action: 'Open KPI setup',
      note: 'Performance page is a roll-up. Authoring of KPI definitions = Contract Detail → KPI Setup. Composite score = nightly Cognara recompute.',
    },
  },

  // ────────── Amendments & Change Orders ──────────
  'cm-amendment-types': {
    title: 'Amendment Types & Triggers',
    externals: [_CM_EXT.fidic, _CM_EXT.aia],
    internals: [
      { label: 'Amendment Workflow', path: '/app/cm/amendment-workflow', why: 'Each type has a workflow' },
      { label: 'Change Order Wizard', path: '/app/cm/change-order-wizard', why: 'Create an amendment' },
    ],
    authoring: {
      where: 'System Config → Amendment Types (Admin)',
      path: '/app/admin/system-config',
      action: 'Manage types',
      note: 'Reference page. Type taxonomy seeded in clm_setup_seed.py. Admins can extend via System Config.',
    },
  },
  'cm-amendment-workflow': {
    title: 'Amendment Workflow',
    externals: [_CM_EXT.fidic],
    internals: [
      { label: 'Amendment Types', path: '/app/cm/amendment-types', why: 'Type drives the workflow' },
      { label: 'Internal Review', path: '/app/cm/internal-review', why: 'Re-uses review workflow engine' },
    ],
    authoring: {
      where: 'Admin → Workflows (re-uses Review Workflow Builder)',
      path: '/app/admin/workflows',
      action: 'Edit amendment workflow',
      note: 'Workflow templates authored once in Admin. Each amendment instance gets its own state in `contractAmendments.workflowState`.',
    },
  },
  'cm-change-order-wizard': {
    title: 'Change Order / Variation Wizard',
    externals: [_CM_EXT.fidic, _CM_EXT.aia],
    internals: [
      { label: 'Amendments', path: '/app/cm/amendments', why: 'Wizard creates entries here' },
      { label: 'Cost & Schedule Impact', path: '/app/cm/cost-impact', why: 'Impact tracked from the change' },
      { label: 'Claims & LD', path: '/app/claims', why: 'CO may rebut a claim' },
    ],
    authoring: {
      where: 'Change Order Wizard (this page)',
      path: '/app/cm/change-order-wizard',
      action: 'Start new change order',
      note: 'Change Orders / Variations authored here. Stored in `contractAmendments` (consolidating legacy `variations`). Multi-stage approval via amendment workflow.',
    },
  },
  'cm-cost-impact': {
    title: 'Cost & Schedule Impact Tracker',
    externals: [{ label: 'PMI Earned Value Management', url: 'https://www.pmi.org', note: 'EVM methodology' }],
    internals: [
      { label: 'Change Order Wizard', path: '/app/cm/change-order-wizard', why: 'Source of impacts' },
      { label: 'Financial Monitoring', path: '/app/cm/financial-monitoring', why: 'Aggregate impacts on contract value' },
    ],
    authoring: {
      where: 'Read-only — derived from Change Orders + Schedule',
      path: '/app/cm/change-order-wizard',
      action: 'Author a change order',
      note: 'No direct authoring. Numbers derive from `contractAmendments[*].costImpact` and `.scheduleImpactDays`.',
    },
  },
  'cm-variation-log': {
    title: 'Variation Log (Contractor-initiated)',
    externals: [_CM_EXT.fidic],
    internals: [
      { label: 'Change Order Wizard', path: '/app/cm/change-order-wizard', why: 'Variation log entries become COs' },
      { label: 'Claims & LD', path: '/app/claims', why: 'Refused VOs may escalate to claims' },
    ],
    authoring: {
      where: 'Contractor (Supplier Portal) or PM on their behalf',
      path: '/app/cm/change-order-wizard',
      action: 'Log variation',
      note: 'Variations authored by contractor in Supplier Portal (`/portal`) or by PM. Stored in `contractAmendments` with type=CONTRACTOR_INITIATED.',
    },
  },
  'cm-amendments': {
    title: 'Amendments — AI Impact + Workflow',
    externals: [_CM_EXT.fidic, _CM_EXT.aia],
    internals: [
      { label: 'Change Order Wizard', path: '/app/cm/change-order-wizard', why: 'Primary authoring surface' },
      { label: 'Amendment Workflow', path: '/app/cm/amendment-workflow', why: 'Approval routing' },
    ],
    authoring: {
      where: 'Change Order Wizard (data) + AI engine (impact analysis)',
      path: '/app/cm/change-order-wizard',
      action: 'Open new CO',
      note: 'Data authored in CO Wizard. AI impact summary authored by /api/contracts/v2/{id}/amendments/{aid}/ai-impact (Cognara). Stored in `contractAmendments.aiImpact`.',
    },
  },

  // ────────── Renewal & Expiry ──────────
  'cm-expiry-alerts': {
    title: 'Expiry Detection & Alerts',
    externals: [{ label: 'iCalendar RFC 5545', url: 'https://www.rfc-editor.org/rfc/rfc5545', note: 'Calendar reminders spec' }],
    internals: [
      { label: 'Renewal Intelligence', path: '/app/cm/renewal-intelligence', why: 'Alerts → decision queue' },
      { label: 'Renewal AI', path: '/app/cm/renewal', why: 'Acting on the alert' },
    ],
    authoring: {
      where: 'Auto — generated by expiry cron (clm_crons.py)',
      path: '/app/cm/renewal',
      action: 'Review renewal queue',
      note: 'Alerts authored automatically by `expiry_alert_cron` (T-90 / T-60 / T-30 / T-7 days). Stored in `notifications` + `renewalQueue`.',
    },
  },
  'cm-renewal-intelligence': {
    title: 'Renewal Decision Intelligence',
    externals: [_CM_EXT.uncitral],
    internals: [
      { label: 'Renewal AI', path: '/app/cm/renewal', why: 'Execute the decision' },
      { label: 'Sourcing Events', path: '/app/sourcing/events', why: 'Renegotiate → new event' },
      { label: 'Supplier 360', path: '/app/srm/registration', why: 'Performance feeds the recommendation' },
    ],
    authoring: {
      where: 'AI engine (Cognara) + PM disposition',
      path: '/app/cm/renewal',
      action: 'Open renewal recommendation',
      note: 'AI authors the recommendation (RENEW / RENEGOTIATE / EXIT / EXTEND). PM authors disposition. Stored in `contractRenewals`.',
    },
  },
  'cm-contract-closure': {
    title: 'Contract Expiry & Closure',
    externals: [_CM_EXT.fidic],
    internals: [
      { label: 'Portfolio Dashboard', path: '/app/cm/portfolio', why: 'Closure roll-up' },
      { label: 'Bonds & BG', path: '/app/cm/bonds', why: 'Bonds released on closure' },
    ],
    authoring: {
      where: 'Contract Detail → Close Contract action',
      path: '/app/cm/repository',
      action: 'Open contract → Close',
      note: 'Closure authored by PM action. State machine moves contract to CLOSED. Auto-releases bonds; final invoice gate enforced.',
    },
  },
  'cm-renewal': {
    title: 'Renewal AI · Clone · Sourcing Event',
    externals: [_CM_EXT.uncitral, _CM_EXT.fidic],
    internals: [
      { label: 'Renewal Intelligence', path: '/app/cm/renewal-intelligence', why: 'AI recommendation upstream' },
      { label: 'Sourcing Events', path: '/app/sourcing/events', why: 'Renegotiate spawns a sourcing event' },
      { label: 'Contract Repository', path: '/app/cm/repository', why: 'Cloned contract lands here' },
    ],
    authoring: {
      where: 'Renewal page — clone / extend / renegotiate action',
      path: '/app/cm/renewal',
      action: 'Trigger renewal action',
      note: 'PM authors the action. CLONE → new DRAFT contract (POST /api/contracts/v2/{id}/renew/clone). RENEGOTIATE → new sourcing event. Stored in `contractRenewals`.',
    },
  },
};

// Merge CM_LINKS into the main registry
Object.assign(MODULE_LINKS, CM_LINKS);

const _CM_GENERIC = {
  // Reused as default for any cm/* path without a dedicated entry
  title: 'Contract Lifecycle Management',
  externals: [_CM_EXT.fidic, _CM_EXT.uncitral, _CM_EXT.ica1872],
  internals: [
    { label: 'Contract Repository', path: '/app/cm/repository', why: 'All contracts live here' },
    { label: 'Portfolio Dashboard', path: '/app/cm/portfolio', why: 'Renewals · Bonds · Insurance' },
    { label: 'Integration Hub', path: '/app/cm/integrations', why: 'Cross-module 360 view' },
  ],
  authoring: {
    where: 'New Contract Wizard or Sourcing Award handoff',
    path: '/app/cm/repository/new',
    action: 'New contract',
    note: 'Contracts originate either from manual intake (Wizard) or auto-handoff from Sourcing Award (POST /api/contracts/v2/from-sourcing-award).',
  },
};
MODULE_LINKS['cm-generic'] = _CM_GENERIC;

// Aliases — same payload, multiple keys
MODULE_LINKS['purchase-orders'] = MODULE_LINKS.po;
MODULE_LINKS.p2p = MODULE_LINKS.requisitions;

// ═══════════════════════ GOODS RECEIPT (GR-7) ═══════════════════════
MODULE_LINKS.grn = {
  title: 'Goods Receipt (GR-7)',
  externals: [
    { label: 'India e-Way Bill Portal', url: 'https://ewaybillgst.gov.in', note: 'In-transit goods verification & e-Way Bill validity at the dock' },
    { label: 'GS1 / Barcode Lookups', url: 'https://www.gs1.org/standards/barcodes', note: 'GTIN/SSCC validation for ASN + receipt scanning' },
    { label: 'ANSI/ASQ Z1.4 Sampling Tables', url: 'https://asq.org/quality-resources/z1-4-z1-9', note: 'AQL acceptance sampling plans for incoming quality inspection' },
    { label: 'ASTM Standards Catalogue', url: 'https://www.astm.org/Standards/standards-and-publications.html', note: 'Material test standards referenced by MTRs' },
    { label: 'India FSSAI Compliance', url: 'https://www.fssai.gov.in', note: 'Food & ingredient receipt compliance (shelf-life + cert validity)' },
    { label: 'CDSCO Pharma Imports', url: 'https://cdsco.gov.in', note: 'Pharma / API receipts — CoA + import licence' },
    { label: 'GHS Safety Data Sheets', url: 'https://www.unece.org/trans/danger/publi/ghs/ghs_welcome_e.html', note: 'Hazmat MSDS / SDS classification at receipt' },
    { label: 'Indian Customs ICEGATE', url: 'https://www.icegate.gov.in', note: 'Bill-of-Entry reconciliation for imports' },
  ],
  internals: [
    { label: 'Purchase Orders', path: '/app/po', why: 'GRN anchors back to PO lines; receivedQty writes back to PO' },
    { label: 'Invoices & Payment', path: '/app/invoices', why: '3-way match consumes GRN qty; GR/IR accrual cleared on invoice post' },
    { label: 'Performance Mgmt', path: '/app/srm/performance', why: 'OTD + quality KPIs from GRN feed supplier scorecard' },
    { label: 'Catalogue', path: '/app/catalogue', why: 'Item specs reference quality inspection criteria' },
    { label: 'Category & Spend', path: '/app/scm/taxonomy', why: 'Category attributes drive inspection rules, batch/serial, shelf-life' },
    { label: 'Claims & LD', path: '/app/claims', why: 'Late delivery + rejections may trigger LD claims' },
    { label: 'Contracts', path: '/app/contracts', why: 'Delivery clauses + acceptance criteria validated at GRN' },
  ],
  authoring: {
    where: 'GRN Workbench (this page) — Receiving Clerk',
    path: '/app/grn/receipt',
    action: 'Post a goods receipt',
    note: 'GRN documents authored here (manual receipt or ASN-driven). Stored in `goods_receipts`. Triggers GR/IR accrual + 3-way match availability.',
  },
};

// ═══════════════════════ INVOICES & PAYMENT (IP-8) ═══════════════════════
MODULE_LINKS.invoice = {
  title: 'Invoices & Payment (IP-8)',
  externals: [
    { label: 'India GST IRN / e-Invoice Portal', url: 'https://einvoice1.gst.gov.in', note: 'IRN & QR validation for B2B invoices (turnover threshold)' },
    { label: 'India GST Returns (GSTN)', url: 'https://www.gst.gov.in', note: 'GSTR-2A/2B reconciliation + ITC eligibility' },
    { label: 'GST HSN/SAC Search', url: 'https://services.gst.gov.in/services/searchhsnsac', note: 'Authoritative HSN/SAC to tax rate mapping' },
    { label: 'NPCI NACH', url: 'https://www.npci.org.in/what-we-do/nach/product-overview', note: 'Bulk credit / payouts via NACH' },
    { label: 'SWIFT Codes Directory', url: 'https://www2.swift.com/swift-bic/', note: 'BIC / SWIFT validation for cross-border payments' },
    { label: 'PEPPOL Network Directory', url: 'https://directory.peppol.eu', note: 'EU e-invoicing access points + signature validation' },
    { label: 'OFAC SDN List', url: 'https://sanctionssearch.ofac.treas.gov', note: 'Sanctions screening of supplier bank account changes' },
    { label: 'Razorpay Dashboard', url: 'https://dashboard.razorpay.com', note: 'API-driven payout test/live mode' },
    { label: 'RBI FX Reference Rates', url: 'https://www.rbi.org.in/Scripts/ReferenceRateArchive.aspx', note: 'INR FX conversion at payment time' },
    { label: 'Resend (Remittance Email)', url: 'https://resend.com', note: 'Transactional email for remittance advice + dispute notices' },
  ],
  internals: [
    { label: 'Purchase Orders', path: '/app/po', why: 'Invoice anchors to PO for 3-way match + price/qty tolerance' },
    { label: 'Goods Receipt', path: '/app/grn', why: 'GRN qty is the third leg of the 3-way match' },
    { label: 'Contracts', path: '/app/contracts', why: 'Price-cap guard validates invoice against contract pricing' },
    { label: 'Supplier 360', path: '/app/srm/registration', why: 'Bank account on file + change-detection drives fraud signal' },
    { label: 'Performance Mgmt', path: '/app/srm/performance', why: 'Match-rate, on-time-pay, dispute count feed scorecards' },
    { label: 'Spend Cube (Analytics)', path: '/app/scm/spend-cube', why: 'Posted invoices populate actual-spend facts' },
    { label: 'Budget Reservation', path: '/app/requisitions', why: 'Paid invoice releases the PR-anchored budget reservation' },
    { label: 'Catalogue', path: '/app/catalogue', why: 'Off-catalogue / off-contract invoices flagged as maverick' },
    { label: 'Category & Spend', path: '/app/scm/taxonomy', why: 'Category attributes drive HSN, tax rate, and price tolerance' },
  ],
  authoring: {
    where: 'Invoice Workbench (this page) · Supplier Portal upload · OCR/PEPPOL ingest',
    path: '/app/invoices',
    action: 'Upload / capture invoice',
    note: 'Invoices originate from supplier portal, AP team upload, OCR ingestion, or PEPPOL e-invoicing. Stored in `invoices`. 3-way match auto-runs on capture.',
  },
};
MODULE_LINKS.ap = MODULE_LINKS.invoice;            // alias for /app/ap callers
MODULE_LINKS['invoices'] = MODULE_LINKS.invoice;    // alias

export function getModuleLinks(key) {
  return MODULE_LINKS[key] || null;
}
